import { BaseScene } from './BaseScene';
import { SceneKey } from './sceneKeys';
import { GAME_WIDTH } from './gameConfig';
import {
  BATTLE_LAYOUT,
  BATTLE_COLORS,
  BATTLE_INITIAL,
  DISTANCE_LABELS,
  STANCE_LABELS,
  DISTANCE_CHARACTER_POSITIONS,
  COMMAND_LABELS,
  COMMAND_UI_LAYOUT,
  COMMAND_UI_COLORS,
  COMMAND_BUTTON_ROWS,
  SURRENDER_MESSAGES,
  COMMAND_COUNTDOWN_SECONDS,
  COMMAND_COUNTDOWN_WARNING_THRESHOLD,
  INDICATOR_LAYOUT,
  SPECTATOR_AUTO_TURN_DELAY_MS,
  SPECTATOR_MESSAGES,
  formatTime,
  clampHp,
  getCommandButtonLayout,
} from './battleConfig';
import { detectDevice } from '../utils/deviceDetector';
import { logBattleStart, logBattleEnd } from '../utils/firebaseAnalytics';
import {
  getSpecialIndicatorColor,
  getReflectorIndicatorColor,
  INDICATOR_CHARS,
  INDICATOR_COLORS,
} from '../utils/abilityIndicator';
import { DistanceType } from '../types/Distance';
import { StanceType } from '../types/Stance';
import { CommandType } from '../types/Command';
import { BattleState, TurnResult } from '../types/BattleState';
import { Monster } from '../types/Monster';
import { TurnCommands } from '../types/Command';
import { MONSTER_DATABASE, getMonsterById } from '../constants/monsters';
import { FINAL_MONSTER_DATABASE, getMonsterWithGrownStats, MAX_GROWTH_STAGES } from '../constants/monsterStats';
import { INITIAL_MONSTER_ID } from './characterSelectConfig';
import { getChallengeStage, getChallengeBgKey } from '../constants/challengeConfig';
import { CommandSelectionManager } from '../battle/CommandSelectionManager';
import { processTurn } from '../battle/turnProcessor';
import { resolveBattleEffects } from '../battle/effectResolver';
import { BattleEffectPlayer } from './BattleEffectPlayer';
import { selectCommands, AILevel } from '../ai';
import { selectLv6Core } from '../ai/lv6Core';
import { mulberry32, generateSeed } from '../ai/random';
import { DEFAULT_AI_CONFIG } from '../ai/types';

/**
 * AIレベルに応じて同期的にコマンド選択する統合エントリ
 *
 * Lv1-5 は selectCommands（同期）を呼び、Lv6 は selectLv6Core を
 * インライン同期実行する（Worker なし、~50ms 程度）。
 *
 * UI スレッドで呼ばれるため Promise を返さない設計。
 */
function selectCommandsForBattle(
  state: import('../types').BattleState,
  playerId: 'player1' | 'player2',
  monster: import('../types').Monster,
  level: AILevel,
  opponentMonster: import('../types').Monster,
  turnHistory: import('../types').TurnResult[],
  aiConfig?: import('../ai/types').AIConfig,
): import('../types').TurnCommands {
  if (level === AILevel.LV6) {
    const seed = generateSeed();
    const config = aiConfig ?? DEFAULT_AI_CONFIG;
    return selectLv6Core(state, playerId, monster, opponentMonster, turnHistory, config, mulberry32(seed));
  }
  return selectCommands(state, playerId, monster, level, Math.random, opponentMonster, turnHistory, aiConfig);
}
import { AIConfig } from '../ai/types';
import { checkVictoryAfterTurn, checkVictoryOnGiveUp, checkVictoryOnTimeout } from '../battle/victoryCondition';
import { GameMode } from '../types/GameMode';
import { TutorialManager } from '../battle/TutorialManager';
import { GAME_HEIGHT } from './gameConfig';
import { SocketClient } from '../network/SocketClient';
import { BattleResult, BattleResultType } from '../types/BattleState';
import { playBgm, playSe, stopAllSe, stopBgm } from '../utils/audioManager';
import { AudioKey } from '../constants/audioKeys';
import { getSurvivalBgmKey, getSurvivalBgKey } from '../utils/survivalBgmRotation';
import { getMonsterBattleKey, BackgroundImageKey } from '../constants/imageKeys';
import { loadSettings } from '../utils/settingsManager';
import { adjustResultForPlayer, buildPlayerBattleResult } from '../utils/battleResultUtils';
import { THEME_COLORS } from './characterSelectConfig';
import { recordSurvivalWin, saveSurvivalData } from '../utils/survivalManager';
import { TournamentMode, TournamentState } from '../types/Tournament';
import { recordMatchResult, advanceEliminationBracket } from '../tournament/tournamentManager';
import {
  PartyMemberState,
  getNextPartyIndex,
  updatePartyAfterBattle,
  isPartyDefeated,
  determine3v3BattleOutcome,
} from '../battle/partyBattle';
import {
  EREBOS_MONSTER,
  EREBOS_FORM2_MONSTER,
  isBossMonster,
  EREBOS_AI_CONFIG,
  BOSS_STAGE_NUMBER,
  BOSS_DISPLAY_SCALE,
} from '../constants/bossConfig';
import { getBossCommandMods } from '../ai/bossModifiers';
import { selectLv6Async } from '../ai/lv6Async';
import { getMonsterIdsForPreload } from './battlePreloadUtils';
import { isDebugBossWeakened } from '../utils/debugManager';
export { adjustResultForPlayer };

/** BattleSceneに渡されるデータ */
export interface BattleSceneData {
  monsterId?: string;
  enemyMonsterId?: string;
  aiLevel?: AILevel;
  /** 観戦モード: CPU2のAIレベル */
  enemyAiLevel?: AILevel;
  mode?: GameMode;
  stageNumber?: number;
  clearedStages?: number;
  // ネットワークモード用
  isNetworkMode?: boolean;
  roomId?: string;
  socketClient?: SocketClient;
  playerNumber?: 1 | 2;
  playerMonster?: Monster;
  enemyMonster?: Monster;
  initialBattleState?: BattleState;
  // サバイバルモード用
  survivalSaveData?: import('../types/SurvivalSaveData').SurvivalSaveData;
  // 大会モード用
  tournamentState?: import('../types/Tournament').TournamentState;
  tournamentMatchIndex?: number;
  tournamentPlayerIsP1?: boolean;
  tournamentDrawRetryCount?: number;
  // カスタムAI用
  /** プレイヤー側（player1）のカスタムAI設定。設定時はプレイヤー入力の代わりにAIが自動操作 */
  playerAIConfig?: AIConfig;
  /** 観戦モードCPU1のカスタムAI設定 */
  enemyAIConfig?: AIConfig;
  // 3vs3用
  playerParty?: PartyMemberState[];
  enemyParty?: PartyMemberState[];
  playerPartyIndex?: number;
  enemyPartyIndex?: number;
  // BGM自由選択（FREE_CPU/FREE_CPU_3V3/FREE_LOCAL用）
  selectedBgmKey?: string;
  // バトルマップ選択（FREE_CPU/FREE_CPU_3V3/FREE_LOCAL用）
  selectedBattleBg?: string;
  // キャンペーン再プレイフラグ
  isReplay?: boolean;
  // ボスステージフラグ（Stage 9）
  isBossStage?: boolean;
  // ボス形態変化フラグ（第1形態撃破→第2形態への遷移時のみ true）
  isFormChange?: boolean;
  // 隠しキャラ挑戦戦フラグ
  isHiddenChallenge?: boolean;
  /** 挑戦戦の対象となる隠しキャラID（'alice' | 'mococo'）*/
  challengeTargetId?: string;
}

/**
 * バトル画面シーン
 *
 * HPバー、キャラ表示、距離表示、残り時間、スタンス表示、コマンド選択UIを管理する。
 */
export class BattleScene extends BaseScene {
  // モンスターデータ
  private playerMonster!: Monster;
  private enemyMonster!: Monster;

  // バトル状態
  private currentDistance!: DistanceType;
  private currentPlayerStance!: StanceType;
  private currentEnemyStance!: StanceType;
  private remainingTime!: number;
  private playerCurrentHp!: number;
  private enemyCurrentHp!: number;

  // UIオブジェクト - HPバー
  private playerHpBarBg!: Phaser.GameObjects.Rectangle;
  private playerHpBarFill!: Phaser.GameObjects.Rectangle;
  private playerHpText!: Phaser.GameObjects.Text;
  private enemyHpBarBg!: Phaser.GameObjects.Rectangle;
  private enemyHpBarFill!: Phaser.GameObjects.Rectangle;
  private enemyHpText!: Phaser.GameObjects.Text;

  // UIオブジェクト - キャラ表示
  private playerCharacterText!: Phaser.GameObjects.Text;
  private enemyCharacterText!: Phaser.GameObjects.Text;
  private playerCharacterImage?: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite;
  private enemyCharacterImage?: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite;

  // UIオブジェクト - ステータス
  private distanceText!: Phaser.GameObjects.Text;
  private timeText!: Phaser.GameObjects.Text;
  private playerStanceText!: Phaser.GameObjects.Text;
  private enemyStanceText!: Phaser.GameObjects.Text;

  // コマンド選択UI
  private commandManager!: CommandSelectionManager;
  private commandButtons: Map<CommandType, { bg: Phaser.GameObjects.Image; text: Phaser.GameObjects.Text }> = new Map();
  private selectionText1st!: Phaser.GameObjects.Text;
  private selectionText2nd!: Phaser.GameObjects.Text;
  private confirmButtonBg!: Phaser.GameObjects.Rectangle;
  private confirmButtonText!: Phaser.GameObjects.Text;
  private cancelButtonBg!: Phaser.GameObjects.Rectangle;
  private cancelButtonText!: Phaser.GameObjects.Text;

  // 能力残回数インジケーター
  private specialIndicatorFilledText?: Phaser.GameObjects.Text;
  private specialIndicatorEmptyText?: Phaser.GameObjects.Text;
  private reflectorIndicatorFilledText?: Phaser.GameObjects.Text;
  private reflectorIndicatorEmptyText?: Phaser.GameObjects.Text;

  // バトルロジック
  private battleState!: BattleState;
  private effectPlayer!: BattleEffectPlayer;
  private turnHistory: TurnResult[] = [];
  private isPlayingEffects = false;
  private enemyAILevel: AILevel = AILevel.LV2;
  private gameMode?: GameMode;
  private stageNumber?: number;
  private clearedStages?: number;
  private isReplay?: boolean;
  private survivalSaveData?: import('../types/SurvivalSaveData').SurvivalSaveData;

  // 二重タップ防止フラグ（ローカルモード用）
  private isConfirmProcessing = false;

  // ネットワークモード
  private isNetworkMode = false;
  private roomId?: string;
  private socketClient?: SocketClient;
  private playerNumber?: 1 | 2;
  private isWaitingForOpponent = false;
  private waitingText?: Phaser.GameObjects.Text;
  private pendingBattleFinishedPayload?: import('../../shared/types/SocketEvents').BattleFinishedPayload;
  private hasNetworkBattleTransitioned = false;

  // コマンドカウントダウン（ネットワークモード用）
  private commandTimeoutTimer?: Phaser.Time.TimerEvent;
  private commandCountdownText?: Phaser.GameObjects.Text;
  // コマンド入力中フラグ（SURVIVALモードでのタイマー停止に使用）
  private isCommandInputActive = false;
  private commandCountdownSeconds = COMMAND_COUNTDOWN_SECONDS;

  // ギブアップUI
  private surrenderButtonBg!: Phaser.GameObjects.Rectangle;
  private surrenderButtonText!: Phaser.GameObjects.Text;
  private surrenderConfirmOverlay?: Phaser.GameObjects.Rectangle;
  private surrenderConfirmPanel?: Phaser.GameObjects.Rectangle;
  private surrenderConfirmTitle?: Phaser.GameObjects.Text;
  private surrenderConfirmBody?: Phaser.GameObjects.Text;
  private surrenderConfirmYesBg?: Phaser.GameObjects.Rectangle;
  private surrenderConfirmYesText?: Phaser.GameObjects.Text;
  private surrenderConfirmNoBg?: Phaser.GameObjects.Rectangle;
  private surrenderConfirmNoText?: Phaser.GameObjects.Text;

  // チュートリアル
  private tutorialManager!: TutorialManager;
  private tutorialPopupOverlay?: Phaser.GameObjects.Rectangle;
  private tutorialPopupText?: Phaser.GameObjects.Text;
  private tutorialPopupDetailText?: Phaser.GameObjects.Text;
  private tutorialPopupStepText?: Phaser.GameObjects.Text;
  private tutorialPopupHint?: Phaser.GameObjects.Text;
  private tutorialHighlightTweens: Phaser.Tweens.Tween[] = [];

  // 観戦モード
  private isSpectatorMode = false;
  private enemyAILevel2: AILevel = AILevel.LV2;
  private spectatorAutoTurnTimer?: Phaser.Time.TimerEvent;

  // カスタムAIモード
  /** プレイヤー側をカスタムAIで自動操作するモード */
  private isAutoPlayerMode = false;
  private playerAIConfig?: AIConfig;
  private enemyAIConfig?: AIConfig;

  // 大会モード
  private tournamentState?: import('../types/Tournament').TournamentState;
  private tournamentMatchIndex?: number;
  private tournamentPlayerIsP1?: boolean;
  private tournamentDrawRetryCount: number = 0;

  // 3vs3モード
  private playerParty: PartyMemberState[] = [];
  private enemyParty: PartyMemberState[] = [];
  private playerPartyIndex = 0;
  private enemyPartyIndex = 0;

  // ボスステージ
  private isBossStage = false;
  private currentBattleBgKey: string | null = null;

  // 隠しキャラ挑戦戦
  private isHiddenChallenge = false;
  private challengeTargetId?: string;

  // preload() でモンスター ID を参照するために init() で保持
  private initData?: BattleSceneData;

  constructor() {
    super(SceneKey.BATTLE);
  }

  init(data?: BattleSceneData): void {
    this.initData = data;
  }

  preload(): void {
    const availableIds = Object.keys(BattleScene.SPRITESHEET_ANIMS);
    const monsterIds = getMonsterIdsForPreload(this.initData, availableIds);

    const neededKeys = new Set<string>();
    for (const monsterId of monsterIds) {
      const sheetDef = BattleScene.SPRITESHEET_ANIMS[monsterId];
      if (sheetDef) {
        for (const sheet of sheetDef.sheets) {
          neededKeys.add(sheet.key);
        }
      }
    }
    // エレボス変身カットシーン用スプライト（SPRITESHEET_ANIMS 外のため個別追加）
    // 第1形態バトル or 第2形態開始時（isFormChange）に必要
    if (monsterIds.includes('erebos') || this.initData?.isFormChange) {
      neededKeys.add('ss_erebos_transform');
    }

    const toLoad = Object.entries(BattleScene.SPRITESHEET_PATHS)
      .filter(([key]) => neededKeys.has(key) && !this.textures.exists(key));
    if (toLoad.length === 0) return;

    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;
    this.add.rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, 0x000000);
    this.add.text(cx, cy - 40, 'バトル準備中...', {
      fontSize: '22px',
      color: '#ffffff',
      fontFamily: "'Noto Sans JP', Arial, sans-serif",
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5);
    this.add.rectangle(cx, cy + 10, 404, 20, 0x333355).setOrigin(0.5);
    const bar = this.add.rectangle(cx - 200, cy + 10, 0, 16, 0x6688ff).setOrigin(0, 0.5);
    this.load.on('progress', (v: number) => { bar.setSize(400 * v, 16); });
    this.load.on('loaderror', (file: { key: string }) => {
      console.warn(`[BattleScene] スプライトロード失敗: ${file.key}`);
    });

    for (const [key, path] of toLoad) {
      this.load.spritesheet(key, path, { frameWidth: 512, frameHeight: 512 });
    }
  }

  create(data?: BattleSceneData): void {
    const isSurvival = data?.mode === GameMode.SURVIVAL && data?.survivalSaveData != null;
    const survivalBgKey = isSurvival ? getSurvivalBgKey(data!.survivalSaveData!.winCount) : null;
    const isChallenge = data?.mode === GameMode.CHALLENGE && data?.stageNumber != null;
    const challengeBgKey = isChallenge ? getChallengeBgKey(data!.stageNumber!) : null;
    const RANDOM_BG_KEYS = [
      BackgroundImageKey.BATTLE, BackgroundImageKey.BATTLE_2, BackgroundImageKey.BATTLE_3,
      BackgroundImageKey.BATTLE_4, BackgroundImageKey.BATTLE_5, BackgroundImageKey.BATTLE_6,
      BackgroundImageKey.BATTLE_7, BackgroundImageKey.BATTLE_LUNA, BackgroundImageKey.BATTLE_ARENA,
    ];
    const resolvedBg = data?.selectedBattleBg === 'bg_random'
      ? RANDOM_BG_KEYS[Math.floor(Math.random() * RANDOM_BG_KEYS.length)]
      : data?.selectedBattleBg;
    const battleBgKey = survivalBgKey ?? resolvedBg ?? challengeBgKey ?? BackgroundImageKey.BATTLE;
    const bgKey = this.textures.exists(battleBgKey) ? battleBgKey : BackgroundImageKey.BATTLE;
    this.currentBattleBgKey = bgKey;
    if (this.textures.exists(bgKey)) {
      this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, bgKey).setOrigin(0.5);
    }
    this.gameMode = data?.mode;
    this.stageNumber = data?.stageNumber;
    this.clearedStages = data?.clearedStages;
    this.isReplay = data?.isReplay;
    this.survivalSaveData = data?.survivalSaveData;
    this.tournamentState = data?.tournamentState;
    this.tournamentMatchIndex = data?.tournamentMatchIndex;
    this.tournamentPlayerIsP1 = data?.tournamentPlayerIsP1;
    this.tournamentDrawRetryCount = data?.tournamentDrawRetryCount ?? 0;
    this.playerParty = data?.playerParty ?? [];
    this.enemyParty = data?.enemyParty ?? [];
    this.playerPartyIndex = data?.playerPartyIndex ?? 0;
    this.enemyPartyIndex = data?.enemyPartyIndex ?? 0;
    this.isBossStage = data?.isBossStage ?? false;
    this.isHiddenChallenge = data?.isHiddenChallenge ?? false;
    this.challengeTargetId = data?.challengeTargetId;

    // ネットワークモード設定
    this.isNetworkMode = data?.isNetworkMode === true;
    this.roomId = data?.roomId;
    this.socketClient = data?.socketClient;
    this.playerNumber = data?.playerNumber;
    this.isWaitingForOpponent = false;

    // 観戦モード設定
    this.isSpectatorMode = this.gameMode === GameMode.SPECTATOR;

    // カスタムAIモード設定
    this.playerAIConfig = data?.playerAIConfig;
    this.enemyAIConfig = data?.enemyAIConfig;
    this.isAutoPlayerMode = !this.isNetworkMode && !this.isSpectatorMode && !!this.playerAIConfig;

    if (this.isNetworkMode) {
      this.setupNetworkMode(data!);
    } else if (this.gameMode === GameMode.SURVIVAL && this.survivalSaveData) {
      this.setupSurvivalMode(data!);
    } else if (this.gameMode === GameMode.CHALLENGE && this.stageNumber) {
      if (this.isBossStage && this.playerParty.length > 0) {
        this.setup3v3Mode(data);
      } else {
        this.setupChallengeMode(data);
      }
    } else {
      this.enemyAILevel = data?.aiLevel ?? AILevel.LV2;
      if (this.isSpectatorMode) {
        this.enemyAILevel2 = data?.enemyAiLevel ?? AILevel.LV2;
      }
      this.setupNormalMode(data);
    }

    // 初期状態設定
    if (this.isNetworkMode && data?.initialBattleState) {
      const state = data.initialBattleState;
      // Player2視点の場合、自分がplayer2、敵がplayer1
      if (this.playerNumber === 2) {
        this.currentDistance = state.currentDistance;
        this.currentPlayerStance = state.player2.currentStance;
        this.currentEnemyStance = state.player1.currentStance;
        this.remainingTime = state.remainingTime;
        this.playerCurrentHp = state.player2.currentHp;
        this.enemyCurrentHp = state.player1.currentHp;
      } else {
        this.currentDistance = state.currentDistance;
        this.currentPlayerStance = state.player1.currentStance;
        this.currentEnemyStance = state.player2.currentStance;
        this.remainingTime = state.remainingTime;
        this.playerCurrentHp = state.player1.currentHp;
        this.enemyCurrentHp = state.player2.currentHp;
      }
      this.battleState = state;
    } else {
      this.currentDistance = BATTLE_INITIAL.initialDistance;
      this.currentPlayerStance = BATTLE_INITIAL.initialStance;
      this.currentEnemyStance = BATTLE_INITIAL.initialStance;
      this.remainingTime = this.isBossStage
        ? Number.MAX_SAFE_INTEGER
        : (loadSettings().timeLimitSeconds ?? BATTLE_INITIAL.initialTime);
      const is3v3Like = this.gameMode === GameMode.FREE_CPU_3V3 || this.isBossStage;
      const playerMemberHp = is3v3Like
        ? (this.playerParty[this.playerPartyIndex]?.currentHp ?? null)
        : null;
      const enemyMemberHp = is3v3Like
        ? (this.enemyParty[this.enemyPartyIndex]?.currentHp ?? null)
        : null;
      this.playerCurrentHp = playerMemberHp ?? this.playerMonster.stats.hp;
      this.enemyCurrentHp = enemyMemberHp ?? this.enemyMonster.stats.hp;
      if (this.isBossStage && process.env.NODE_ENV !== 'production' && isDebugBossWeakened()) {
        this.enemyCurrentHp = Math.max(1, Math.floor(this.enemyCurrentHp * 0.1));
      }
      this.battleState = this.buildBattleState();
    }

    this.turnHistory = [];
    this.isPlayingEffects = false;
    this.isConfirmProcessing = false;
    logBattleStart(this.gameMode ?? 'unknown', this.playerMonster?.id ?? 'unknown');

    // CommandSelectionManagerを初期化（観戦モードでも内部管理用に生成）
    const playerSide = this.isNetworkMode && this.playerNumber === 2 ? 'player2' : 'player1';
    this.commandManager = new CommandSelectionManager(
      this.battleState,
      playerSide,
      this.playerMonster
    );

    // UI生成
    this.createHpBars();
    this.createCharacterDisplays();
    this.createStatusDisplays();

    if (this.isSpectatorMode || this.isAutoPlayerMode) {
      // 観戦モード / AIオートモード: コマンドUIを非表示、中断ボタンを表示
      this.createSpectatorUI();
    } else {
      this.createCommandUI();
      this.createAbilityIndicators();
      // ギブアップボタン生成
      this.createSurrenderButton();
    }

    // エフェクトプレイヤーを初期化（UI生成後）
    this.effectPlayer = new BattleEffectPlayer(this, {
      playerText: this.playerCharacterText,
      enemyText: this.enemyCharacterText,
      playerImage: this.playerCharacterImage,
      enemyImage: this.enemyCharacterImage,
      playerHpBarFill: this.playerHpBarFill,
      enemyHpBarFill: this.enemyHpBarFill,
      playerMonsterId: this.playerMonster.id,
      enemyMonsterId: this.enemyMonster.id,
      playerOrbColor: this.themeColorToHex(THEME_COLORS[this.playerMonster.id]),
      enemyOrbColor: this.themeColorToHex(THEME_COLORS[this.enemyMonster.id]),
    });

    // チュートリアル初期化（観戦モードではスキップ）
    this.tutorialManager = new TutorialManager(
      this.stageNumber ?? 0,
      this.gameMode ?? GameMode.FREE_CPU
    );
    if (!this.isSpectatorMode && !this.isAutoPlayerMode) {
      if (!this.isNetworkMode) {
        // 初回ターンのコマンド入力待機状態に設定（タイマー停止）
        this.setCommandUIEnabled(true);
      }
      this.startTutorialTurnIfNeeded(this.battleState.currentTurn);
    }

    // ネットワークモード: イベントリスナー設定
    if (this.isNetworkMode && this.socketClient) {
      this.setupNetworkListeners();
      if (!this.isSpectatorMode && !this.isAutoPlayerMode) {
        this.setCommandUIEnabled(true);
      }
    }

    // モバイル横画面推奨プロンプト
    this.setupOrientationPrompt();

    // 観戦モード / AIオートモード: 自動ターン開始
    if (this.isSpectatorMode) {
      this.scheduleSpectatorTurn();
    } else if (this.isAutoPlayerMode) {
      this.scheduleAutoPlayerTurn();
    }

    // バトルBGM再生
    if (this.gameMode === GameMode.SURVIVAL && this.survivalSaveData) {
      playBgm(this.sound, getSurvivalBgmKey(this.survivalSaveData.winCount));
    } else if (this.gameMode === GameMode.TOURNAMENT_ROUND_ROBIN || this.gameMode === GameMode.TOURNAMENT_ELIMINATION) {
      playBgm(this.sound, AudioKey.BGM_TOURNAMENT);
    } else if (this.isBossStage) {
      playBgm(this.sound, AudioKey.BGM_BOSS);
    } else if (data?.selectedBgmKey) {
      const RANDOM_BGM_KEYS = [
        AudioKey.BGM_BATTLE, AudioKey.BGM_TOURNAMENT,
        AudioKey.BGM_SURVIVAL_1, AudioKey.BGM_SURVIVAL_2, AudioKey.BGM_SURVIVAL_3,
      ];
      const resolvedBgm = data.selectedBgmKey === 'bgm_random'
        ? RANDOM_BGM_KEYS[Math.floor(Math.random() * RANDOM_BGM_KEYS.length)]
        : data.selectedBgmKey;
      playBgm(this.sound, resolvedBgm);
    } else {
      playBgm(this.sound, AudioKey.BGM_BATTLE);
    }

    // シーン終了時に再生中のSEを全破棄・隠しキャラリソースを解放
    this.events.once('shutdown', () => {
      stopAllSe();
      this.evictHiddenCharSprites();
    });

    // ボスステージ登場演出
    if (this.isBossStage) {
      if (this.enemyPartyIndex === 0) {
        this.showBossEntranceCutscene();
      } else if (data?.isFormChange) {
        // 第1形態撃破→第2形態への変身時のみカットシーンを表示
        this.showBossTransformCutscene();
      }
    }
  }

  /**
   * Phaserのゲームループ。
   * - CPUモード: 常時タイマーを減算
   * - ネットワークモード: エフェクト再生中のみローカルカウントダウン（コマンド入力中は停止）
   */
  update(_time: number, delta: number): void {
    if (this.isNetworkMode) {
      // エフェクト再生中のみローカルでカウントダウン（仕様: 入力待機中は時計停止）
      if (this.isPlayingEffects && this.battleState && !this.battleState.isFinished) {
        this.remainingTime = Math.max(0, this.remainingTime - delta / 1000);
        this.updateTimeDisplay();
      }
      return;
    }
    if (!this.battleState || this.battleState.isFinished) return;

    // ローカルモード: コマンド入力中はタイマーを停止（プレイヤーの思考時間を圧迫しない）
    // ただし表示上タイムアップ（残り1秒未満）の場合は強制終了へ
    if (this.isCommandInputActive) {
      if (this.isSpectatorMode || this.isAutoPlayerMode || this.remainingTime >= 1) return;
      this.remainingTime = 0;
      this.updateTimeDisplay();
    }

    this.remainingTime = Math.max(0, this.remainingTime - delta / 1000);
    this.updateTimeDisplay();

    // AIオートモードはタイムアウト処理なし（ターン進行は自動スケジュールが管理）
    // 観戦モードはタイムアップで正常終了させるためスキップしない
    if (this.isAutoPlayerMode) return;

    if (this.remainingTime <= 0) {
      this.isPlayingEffects = false;
      this.isConfirmProcessing = false;
      const battleResult = checkVictoryOnTimeout(this.battleState);
      this.battleState = battleResult.finalState; // isFinished=true をセットして多重遷移を防ぐ
      battleResult.turnHistory = this.turnHistory;
      this.setCommandUIEnabled(false);
      this.spectatorAutoTurnTimer?.remove();
      this.transitionToResultWithDown(battleResult, {
        battleResult,
        mode: this.gameMode,
        stageNumber: this.stageNumber,
        clearedStages: this.clearedStages,
        monsterId: this.playerMonster.id,
        isReplay: this.isReplay,
      });
    }
  }

  private setup3v3Mode(_data?: BattleSceneData): void {
    const playerMember = this.playerParty[this.playerPartyIndex];
    const enemyMember = this.enemyParty[this.enemyPartyIndex];
    if (!playerMember || !enemyMember) {
      throw new Error(`3v3: invalid party indices p=${this.playerPartyIndex} e=${this.enemyPartyIndex}`);
    }
    const foundPlayer = FINAL_MONSTER_DATABASE.find((m) => m.id === playerMember.monsterId);
    if (!foundPlayer) {
      throw new Error(`3v3: player monster not found: ${playerMember.monsterId}`);
    }
    this.playerMonster = foundPlayer;

    // ボスモンスターはbossConfigから取得
    const bossMonsters = [EREBOS_MONSTER, EREBOS_FORM2_MONSTER];
    const foundEnemy =
      bossMonsters.find((m) => m.id === enemyMember.monsterId) ??
      FINAL_MONSTER_DATABASE.find((m) => m.id === enemyMember.monsterId);
    if (!foundEnemy) {
      throw new Error(`3v3: enemy monster not found: ${enemyMember.monsterId}`);
    }
    this.enemyMonster = foundEnemy;
    if (this.isBossStage) {
      this.enemyAILevel = AILevel.LV6;
    }
  }

  private setupChallengeMode(data?: BattleSceneData): void {
    const stage = getChallengeStage(this.stageNumber!);
    if (!stage) {
      throw new Error(`Challenge stage ${this.stageNumber} not found`);
    }

    this.enemyAILevel = stage.aiLevel;
    const growthStages = Math.min(this.clearedStages ?? 0, MAX_GROWTH_STAGES);

    // プレイヤーモンスター（成長パラメータ適用）
    const playerMonsterId = data?.monsterId || INITIAL_MONSTER_ID;
    const foundPlayer = getMonsterWithGrownStats(playerMonsterId, growthStages);
    if (!foundPlayer) {
      throw new Error(`Failed to create monster ${playerMonsterId} with growth stage ${growthStages}`);
    }
    this.playerMonster = foundPlayer;

    // 敵モンスター（成長パラメータ適用）
    const foundEnemy = getMonsterWithGrownStats(stage.enemyMonsterId, growthStages);
    if (!foundEnemy) {
      throw new Error(`Failed to create monster ${stage.enemyMonsterId} with growth stage ${growthStages}`);
    }
    this.enemyMonster = foundEnemy;
  }

  private setupNetworkMode(data: BattleSceneData): void {
    if (!data.playerMonster || !data.enemyMonster) {
      throw new Error('Network mode requires playerMonster and enemyMonster');
    }
    this.playerMonster = data.playerMonster;
    this.enemyMonster = data.enemyMonster;
  }

  private setupSurvivalMode(data: BattleSceneData): void {
    if (!data.playerMonster || !data.enemyMonster) {
      throw new Error('Survival mode requires playerMonster and enemyMonster');
    }
    this.playerMonster = data.playerMonster;
    this.enemyMonster = data.enemyMonster;
    this.enemyAILevel = data.aiLevel ?? AILevel.LV1;
  }

  private setupNormalMode(data?: BattleSceneData): void {
    // 3vs3モードの場合はパーティからモンスターを取得
    if (this.gameMode === GameMode.FREE_CPU_3V3) {
      this.setup3v3Mode(data);
      return;
    }
    // モンスター取得（FREE_CPU/SPECTATOR/TOURNAMENT系は最終パラメータを使用）
    const useFinalDb = this.gameMode === GameMode.FREE_CPU || this.gameMode === GameMode.SPECTATOR || this.gameMode === GameMode.TOURNAMENT_ROUND_ROBIN || this.gameMode === GameMode.TOURNAMENT_ELIMINATION;
    const db = useFinalDb ? FINAL_MONSTER_DATABASE : MONSTER_DATABASE;
    const playerMonsterId = data?.monsterId || INITIAL_MONSTER_ID;
    const foundPlayer = db.find((m) => m.id === playerMonsterId);
    if (!foundPlayer) {
      throw new Error(`Monster ${playerMonsterId} not found in database`);
    }
    this.playerMonster = foundPlayer;

    if (data?.enemyMonsterId) {
      const foundEnemy = db.find((m) => m.id === data.enemyMonsterId);
      this.enemyMonster = foundEnemy || this.selectRandomEnemy(this.playerMonster.id, db);
    } else {
      this.enemyMonster = this.selectRandomEnemy(this.playerMonster.id, db);
    }
  }

  private buildBattleState(): BattleState {
    // 3vs3モードの場合、引き継ぎの使用回数を適用する
    const playerMember = (this.gameMode === GameMode.FREE_CPU_3V3 || this.isBossStage)
      ? this.playerParty[this.playerPartyIndex]
      : undefined;
    const enemyMember = (this.gameMode === GameMode.FREE_CPU_3V3 || this.isBossStage)
      ? this.enemyParty[this.enemyPartyIndex]
      : undefined;

    const playerSpecial = playerMember && playerMember.remainingSpecialCount !== null
      ? playerMember.remainingSpecialCount
      : this.playerMonster.stats.specialAttackCount;
    const playerReflect = playerMember ? playerMember.usedReflectCount : 0;
    const enemySpecial = enemyMember && enemyMember.remainingSpecialCount !== null
      ? enemyMember.remainingSpecialCount
      : this.enemyMonster.stats.specialAttackCount;
    const enemyReflect = enemyMember ? enemyMember.usedReflectCount : 0;

    return {
      player1: {
        monsterId: this.playerMonster.id,
        currentHp: this.playerCurrentHp,
        currentStance: this.currentPlayerStance,
        remainingSpecialCount: playerSpecial,
        usedReflectCount: playerReflect,
      },
      player2: {
        monsterId: this.enemyMonster.id,
        currentHp: this.enemyCurrentHp,
        currentStance: this.currentEnemyStance,
        remainingSpecialCount: enemySpecial,
        usedReflectCount: enemyReflect,
      },
      currentDistance: this.currentDistance,
      currentTurn: 1,
      remainingTime: this.remainingTime,
      isFinished: false,
    };
  }

  private selectRandomEnemy(excludeId: string, db: Monster[] = MONSTER_DATABASE): Monster {
    const candidates = db.filter((m) => m.id !== excludeId);
    if (candidates.length === 0) {
      return db[0];
    }
    const index = Math.floor(Math.random() * candidates.length);
    return candidates[index];
  }

  private createHpBars(): void {
    const { hpBarY, hpBarWidth, hpBarHeight, playerHpBarX, enemyHpBarX } = BATTLE_LAYOUT;

    // プレイヤーHPバー
    this.playerHpBarBg = this.add.rectangle(
      playerHpBarX + hpBarWidth / 2,
      hpBarY + hpBarHeight / 2,
      hpBarWidth,
      hpBarHeight,
      BATTLE_COLORS.hpBarBg
    );
    this.playerHpBarFill = this.add.rectangle(
      playerHpBarX + hpBarWidth / 2,
      hpBarY + hpBarHeight / 2,
      hpBarWidth,
      hpBarHeight,
      BATTLE_COLORS.playerHpBar
    );
    this.playerHpText = this.add
      .text(playerHpBarX + hpBarWidth / 2, hpBarY + hpBarHeight + 8, '', {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5, 0);
    // プレイヤー名
    this.add
      .text(playerHpBarX, hpBarY - 8, this.playerMonster.name, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0, 1);

    // 敵HPバー
    this.enemyHpBarBg = this.add.rectangle(
      enemyHpBarX + hpBarWidth / 2,
      hpBarY + hpBarHeight / 2,
      hpBarWidth,
      hpBarHeight,
      BATTLE_COLORS.hpBarBg
    );
    this.enemyHpBarFill = this.add.rectangle(
      enemyHpBarX + hpBarWidth / 2,
      hpBarY + hpBarHeight / 2,
      hpBarWidth,
      hpBarHeight,
      BATTLE_COLORS.enemyHpBar
    );
    this.enemyHpText = this.add
      .text(enemyHpBarX + hpBarWidth / 2, hpBarY + hpBarHeight + 8, '', {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5, 0);
    // 敵名
    this.add
      .text(enemyHpBarX + hpBarWidth, hpBarY - 8, this.enemyMonster.name, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(1, 1);

    // HP表示を初期値で更新
    this.updateHpDisplay();
  }

  /** スプライトシートを持つモンスターIDとアニメ定義 */
  private static readonly SPRITESHEET_ANIMS: Record<string, {
    idleKey: string; // idle用シートキー（スプライトの初期テクスチャ）
    sheets: { key: string; anims: { key: string; frames: number[]; frameRate: number; repeat: number }[] }[];
  }> = {
    zaag: {
      idleKey: 'ss_zaag_idle',
      sheets: [
        { key: 'ss_zaag_idle',    anims: [{ key: 'zaag_idle',   frames: [0, 1, 2],    frameRate: 4, repeat: -1 }] },
        { key: 'ss_zaag_attack',  anims: [{ key: 'zaag_attack', frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_zaag_special', anims: [{ key: 'zaag_special',frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_zaag_move_f',  anims: [{ key: 'zaag_move_f', frames: [0, 1, 2],    frameRate: 8, repeat: -1 }] },
        { key: 'ss_zaag_move_b',  anims: [{ key: 'zaag_move_b', frames: [0, 1, 2],    frameRate: 8, repeat: -1 }] },
        { key: 'ss_zaag_dodge',   anims: [{ key: 'zaag_dodge',  frames: [0, 1, 2],    frameRate: 8, repeat:  0 }] },
        { key: 'ss_zaag_hit',     anims: [{ key: 'zaag_hit',    frames: [0, 1, 2],    frameRate: 6, repeat:  0 }] },
        { key: 'ss_zaag_down',    anims: [{ key: 'zaag_down',   frames: [0, 1, 2],    frameRate: 4, repeat:  0 }] },
      ],
    },
    zephyr: {
      idleKey: 'ss_zephyr_idle',
      sheets: [
        { key: 'ss_zephyr_idle',    anims: [{ key: 'zephyr_idle',   frames: [0, 1, 2],       frameRate: 4, repeat: -1 }] },
        { key: 'ss_zephyr_attack',  anims: [{ key: 'zephyr_attack', frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_zephyr_special', anims: [{ key: 'zephyr_special',frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_zephyr_move_f',  anims: [{ key: 'zephyr_move_f', frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_zephyr_move_b',  anims: [{ key: 'zephyr_move_b', frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_zephyr_dodge',   anims: [{ key: 'zephyr_dodge',  frames: [0, 1, 2],       frameRate: 8, repeat:  0 }] },
        { key: 'ss_zephyr_hit',     anims: [{ key: 'zephyr_hit',    frames: [0, 1, 2],       frameRate: 6, repeat:  0 }] },
        { key: 'ss_zephyr_down',    anims: [{ key: 'zephyr_down',   frames: [0, 1, 2],       frameRate: 4, repeat:  0 }] },
      ],
    },
    balga: {
      idleKey: 'ss_balga_idle',
      sheets: [
        { key: 'ss_balga_idle',    anims: [{ key: 'balga_idle',   frames: [0, 1, 2],       frameRate: 4, repeat: -1 }] },
        { key: 'ss_balga_attack',  anims: [{ key: 'balga_attack', frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_balga_special', anims: [{ key: 'balga_special',frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_balga_move_f',  anims: [{ key: 'balga_move_f', frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_balga_move_b',  anims: [{ key: 'balga_move_b', frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_balga_dodge',   anims: [{ key: 'balga_dodge',  frames: [0, 1, 2],       frameRate: 8, repeat:  0 }] },
        { key: 'ss_balga_hit',     anims: [{ key: 'balga_hit',    frames: [0, 1, 2],       frameRate: 6, repeat:  0 }] },
        { key: 'ss_balga_down',    anims: [{ key: 'balga_down',   frames: [0, 1, 2],       frameRate: 4, repeat:  0 }] },
      ],
    },
    morsu: {
      idleKey: 'ss_morsu_idle',
      sheets: [
        { key: 'ss_morsu_idle',    anims: [{ key: 'morsu_idle',   frames: [0, 1, 2],       frameRate: 4, repeat: -1 }] },
        { key: 'ss_morsu_attack',  anims: [{ key: 'morsu_attack', frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_morsu_special', anims: [{ key: 'morsu_special',frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_morsu_move_f',  anims: [{ key: 'morsu_move_f', frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_morsu_move_b',  anims: [{ key: 'morsu_move_b', frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_morsu_dodge',   anims: [{ key: 'morsu_dodge',  frames: [0, 1, 2],       frameRate: 8, repeat:  0 }] },
        { key: 'ss_morsu_hit',     anims: [{ key: 'morsu_hit',    frames: [0, 1, 2],       frameRate: 6, repeat:  0 }] },
        { key: 'ss_morsu_down',    anims: [{ key: 'morsu_down',   frames: [0, 1, 2],       frameRate: 4, repeat:  0 }] },
      ],
    },
    roona: {
      idleKey: 'ss_roona_idle',
      sheets: [
        { key: 'ss_roona_idle',    anims: [{ key: 'roona_idle',   frames: [0, 1, 2],       frameRate: 4, repeat: -1 }] },
        { key: 'ss_roona_attack',  anims: [{ key: 'roona_attack', frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_roona_special', anims: [{ key: 'roona_special',frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_roona_move_f',  anims: [{ key: 'roona_move_f', frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_roona_move_b',  anims: [{ key: 'roona_move_b', frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_roona_dodge',   anims: [{ key: 'roona_dodge',  frames: [0, 1, 2],       frameRate: 8, repeat:  0 }] },
        { key: 'ss_roona_hit',     anims: [{ key: 'roona_hit',    frames: [0, 1, 2],       frameRate: 6, repeat:  0 }] },
        { key: 'ss_roona_down',    anims: [{ key: 'roona_down',   frames: [0, 1, 2],       frameRate: 4, repeat:  0 }] },
      ],
    },
    gardan: {
      idleKey: 'ss_gardan_idle',
      sheets: [
        { key: 'ss_gardan_idle',    anims: [{ key: 'gardan_idle',   frames: [0, 1, 2],       frameRate: 4, repeat: -1 }] },
        { key: 'ss_gardan_attack',  anims: [{ key: 'gardan_attack', frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_gardan_special', anims: [{ key: 'gardan_special',frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_gardan_move_f',  anims: [{ key: 'gardan_move_f', frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_gardan_move_b',  anims: [{ key: 'gardan_move_b', frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_gardan_dodge',   anims: [{ key: 'gardan_dodge',  frames: [0, 1, 2],       frameRate: 8, repeat:  0 }] },
        { key: 'ss_gardan_hit',     anims: [{ key: 'gardan_hit',    frames: [0, 1, 2],       frameRate: 6, repeat:  0 }] },
        { key: 'ss_gardan_down',    anims: [{ key: 'gardan_down',   frames: [0, 1, 2],       frameRate: 4, repeat:  0 }] },
      ],
    },
    igna: {
      idleKey: 'ss_igna_idle',
      sheets: [
        { key: 'ss_igna_idle',    anims: [{ key: 'igna_idle',   frames: [0, 1, 2],       frameRate: 4, repeat: -1 }] },
        { key: 'ss_igna_attack',  anims: [{ key: 'igna_attack', frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_igna_special', anims: [{ key: 'igna_special',frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_igna_move_f',  anims: [{ key: 'igna_move_f', frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_igna_move_b',  anims: [{ key: 'igna_move_b', frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_igna_dodge',   anims: [{ key: 'igna_dodge',  frames: [0, 1, 2],       frameRate: 8, repeat:  0 }] },
        { key: 'ss_igna_hit',     anims: [{ key: 'igna_hit',    frames: [0, 1, 2],       frameRate: 6, repeat:  0 }] },
        { key: 'ss_igna_down',    anims: [{ key: 'igna_down',   frames: [0, 1, 2],       frameRate: 4, repeat:  0 }] },
      ],
    },
    graon: {
      idleKey: 'ss_graon_idle',
      sheets: [
        { key: 'ss_graon_idle',    anims: [{ key: 'graon_idle',   frames: [0, 1, 2],       frameRate: 4, repeat: -1 }] },
        { key: 'ss_graon_attack',  anims: [{ key: 'graon_attack', frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_graon_special', anims: [{ key: 'graon_special',frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_graon_move_f',  anims: [{ key: 'graon_move_f', frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_graon_move_b',  anims: [{ key: 'graon_move_b', frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_graon_dodge',   anims: [{ key: 'graon_dodge',  frames: [0, 1, 2],       frameRate: 8, repeat:  0 }] },
        { key: 'ss_graon_hit',     anims: [{ key: 'graon_hit',    frames: [0, 1, 2],       frameRate: 6, repeat:  0 }] },
        { key: 'ss_graon_down',    anims: [{ key: 'graon_down',   frames: [0, 1, 2],       frameRate: 4, repeat:  0 }] },
      ],
    },
    erebos: {
      idleKey: 'ss_erebos_idle',
      sheets: [
        { key: 'ss_erebos_idle',    anims: [{ key: 'erebos_idle',    frames: [0, 1, 2],       frameRate: 4, repeat: -1 }] },
        { key: 'ss_erebos_attack',  anims: [{ key: 'erebos_attack',  frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_erebos_special', anims: [{ key: 'erebos_special', frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_erebos_move_f',  anims: [{ key: 'erebos_move_f',  frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_erebos_move_b',  anims: [{ key: 'erebos_move_b',  frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_erebos_dodge',   anims: [{ key: 'erebos_dodge',   frames: [0, 1, 2],       frameRate: 8, repeat:  0 }] },
        { key: 'ss_erebos_hit',     anims: [{ key: 'erebos_hit',     frames: [0, 1, 2],       frameRate: 6, repeat:  0 }] },
        { key: 'ss_erebos_down',    anims: [{ key: 'erebos_down',    frames: [0, 1, 2],       frameRate: 4, repeat:  0 }] },
      ],
    },
    erebos_form2: {
      idleKey: 'ss_erebos_form2_idle',
      sheets: [
        { key: 'ss_erebos_form2_idle',    anims: [{ key: 'erebos_form2_idle',    frames: [0, 1, 2],       frameRate: 4, repeat: -1 }] },
        { key: 'ss_erebos_form2_attack',  anims: [{ key: 'erebos_form2_attack',  frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_erebos_form2_special', anims: [{ key: 'erebos_form2_special', frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_erebos_form2_move_f',  anims: [{ key: 'erebos_form2_move_f',  frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_erebos_form2_move_b',  anims: [{ key: 'erebos_form2_move_b',  frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_erebos_form2_dodge',   anims: [{ key: 'erebos_form2_dodge',   frames: [0, 1, 2],       frameRate: 8, repeat:  0 }] },
        { key: 'ss_erebos_form2_hit',     anims: [{ key: 'erebos_form2_hit',     frames: [0, 1, 2],       frameRate: 6, repeat:  0 }] },
        { key: 'ss_erebos_form2_down',    anims: [{ key: 'erebos_form2_down',    frames: [0, 1, 2],       frameRate: 4, repeat:  0 }] },
      ],
    },
    alice: {
      idleKey: 'ss_alice_idle',
      sheets: [
        { key: 'ss_alice_idle',   anims: [{ key: 'alice_idle',   frames: [0, 1, 2],       frameRate: 4, repeat: -1 }] },
        { key: 'ss_alice_attack', anims: [{ key: 'alice_attack',  frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 },
                                          { key: 'alice_special', frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_alice_move_f', anims: [{ key: 'alice_move_f',  frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_alice_move_b', anims: [{ key: 'alice_move_b',  frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_alice_dodge',  anims: [{ key: 'alice_dodge',   frames: [0, 1, 2],       frameRate: 8, repeat:  0 }] },
        { key: 'ss_alice_hit',    anims: [{ key: 'alice_hit',     frames: [0, 1, 2],       frameRate: 6, repeat:  0 }] },
        { key: 'ss_alice_down',   anims: [{ key: 'alice_down',    frames: [0, 1, 2],       frameRate: 4, repeat:  0 }] },
      ],
    },
    mococo: {
      idleKey: 'ss_mococo_idle',
      sheets: [
        { key: 'ss_mococo_idle',   anims: [{ key: 'mococo_idle',   frames: [0, 1, 2],       frameRate: 4, repeat: -1 }] },
        { key: 'ss_mococo_attack', anims: [{ key: 'mococo_attack',  frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 },
                                           { key: 'mococo_special', frames: [0, 1, 2, 3, 4], frameRate: 8, repeat:  0 }] },
        { key: 'ss_mococo_move_f', anims: [{ key: 'mococo_move_f',  frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_mococo_move_b', anims: [{ key: 'mococo_move_b',  frames: [0, 1, 2],       frameRate: 8, repeat: -1 }] },
        { key: 'ss_mococo_dodge',  anims: [{ key: 'mococo_dodge',   frames: [0, 1, 2],       frameRate: 8, repeat:  0 }] },
        { key: 'ss_mococo_hit',    anims: [{ key: 'mococo_hit',     frames: [0, 1, 2],       frameRate: 6, repeat:  0 }] },
        { key: 'ss_mococo_down',   anims: [{ key: 'mococo_down',    frames: [0, 1, 2],       frameRate: 4, repeat:  0 }] },
      ],
    },
  };

  private static readonly SPRITESHEET_PATHS: Record<string, string> = {
    ss_zaag_idle:    'assets/images/monsters/zaag_idle.webp',
    ss_zaag_attack:  'assets/images/monsters/zaag_attack.webp',
    ss_zaag_special: 'assets/images/monsters/zaag_special.webp',
    ss_zaag_move_f:  'assets/images/monsters/zaag_move_f.webp',
    ss_zaag_move_b:  'assets/images/monsters/zaag_move_b.webp',
    ss_zaag_dodge:   'assets/images/monsters/zaag_dodge.webp',
    ss_zaag_hit:     'assets/images/monsters/zaag_hit.webp',
    ss_zaag_down:    'assets/images/monsters/zaag_down.webp',
    ss_zephyr_idle:    'assets/images/monsters/zephyr_idle.webp',
    ss_zephyr_attack:  'assets/images/monsters/zephyr_attack.webp',
    ss_zephyr_special: 'assets/images/monsters/zephyr_special.webp',
    ss_zephyr_move_f:  'assets/images/monsters/zephyr_move_f.webp',
    ss_zephyr_move_b:  'assets/images/monsters/zephyr_move_b.webp',
    ss_zephyr_dodge:   'assets/images/monsters/zephyr_dodge.webp',
    ss_zephyr_hit:     'assets/images/monsters/zephyr_hit.webp',
    ss_zephyr_down:    'assets/images/monsters/zephyr_down.webp',
    ss_roona_idle:    'assets/images/monsters/roona_idle.webp',
    ss_roona_attack:  'assets/images/monsters/roona_attack.webp',
    ss_roona_special: 'assets/images/monsters/roona_special.webp',
    ss_roona_move_f:  'assets/images/monsters/roona_move_f.webp',
    ss_roona_move_b:  'assets/images/monsters/roona_move_b.webp',
    ss_roona_dodge:   'assets/images/monsters/roona_dodge.webp',
    ss_roona_hit:     'assets/images/monsters/roona_hit.webp',
    ss_roona_down:    'assets/images/monsters/roona_down.webp',
    ss_gardan_idle:    'assets/images/monsters/gardan_idle.webp',
    ss_gardan_attack:  'assets/images/monsters/gardan_attack.webp',
    ss_gardan_special: 'assets/images/monsters/gardan_special.webp',
    ss_gardan_move_f:  'assets/images/monsters/gardan_move_f.webp',
    ss_gardan_move_b:  'assets/images/monsters/gardan_move_b.webp',
    ss_gardan_dodge:   'assets/images/monsters/gardan_dodge.webp',
    ss_gardan_hit:     'assets/images/monsters/gardan_hit.webp',
    ss_gardan_down:    'assets/images/monsters/gardan_down.webp',
    ss_balga_idle:    'assets/images/monsters/balga_idle.webp',
    ss_balga_attack:  'assets/images/monsters/balga_attack.webp',
    ss_balga_special: 'assets/images/monsters/balga_special.webp',
    ss_balga_move_f:  'assets/images/monsters/balga_move_f.webp',
    ss_balga_move_b:  'assets/images/monsters/balga_move_b.webp',
    ss_balga_dodge:   'assets/images/monsters/balga_dodge.webp',
    ss_balga_hit:     'assets/images/monsters/balga_hit.webp',
    ss_balga_down:    'assets/images/monsters/balga_down.webp',
    ss_morsu_idle:    'assets/images/monsters/morsu_idle.webp',
    ss_morsu_attack:  'assets/images/monsters/morsu_attack.webp',
    ss_morsu_special: 'assets/images/monsters/morsu_special.webp',
    ss_morsu_move_f:  'assets/images/monsters/morsu_move_f.webp',
    ss_morsu_move_b:  'assets/images/monsters/morsu_move_b.webp',
    ss_morsu_dodge:   'assets/images/monsters/morsu_dodge.webp',
    ss_morsu_hit:     'assets/images/monsters/morsu_hit.webp',
    ss_morsu_down:    'assets/images/monsters/morsu_down.webp',
    ss_graon_idle:    'assets/images/monsters/graon_idle.webp',
    ss_graon_attack:  'assets/images/monsters/graon_attack.webp',
    ss_graon_special: 'assets/images/monsters/graon_special.webp',
    ss_graon_move_f:  'assets/images/monsters/graon_move_f.webp',
    ss_graon_move_b:  'assets/images/monsters/graon_move_b.webp',
    ss_graon_dodge:   'assets/images/monsters/graon_dodge.webp',
    ss_graon_hit:     'assets/images/monsters/graon_hit.webp',
    ss_graon_down:    'assets/images/monsters/graon_down.webp',
    ss_igna_idle:    'assets/images/monsters/igna_idle.webp',
    ss_igna_attack:  'assets/images/monsters/igna_attack.webp',
    ss_igna_special: 'assets/images/monsters/igna_special.webp',
    ss_igna_move_f:  'assets/images/monsters/igna_move_f.webp',
    ss_igna_move_b:  'assets/images/monsters/igna_move_b.webp',
    ss_igna_dodge:   'assets/images/monsters/igna_dodge.webp',
    ss_igna_hit:     'assets/images/monsters/igna_hit.webp',
    ss_igna_down:    'assets/images/monsters/igna_down.webp',
    ss_erebos_idle:      'assets/images/monsters/erebos_idle.webp',
    ss_erebos_attack:    'assets/images/monsters/erebos_attack.webp',
    ss_erebos_special:   'assets/images/monsters/erebos_special.webp',
    ss_erebos_move_f:    'assets/images/monsters/erebos_move_f.webp',
    ss_erebos_move_b:    'assets/images/monsters/erebos_move_b.webp',
    ss_erebos_dodge:     'assets/images/monsters/erebos_dodge.webp',
    ss_erebos_hit:       'assets/images/monsters/erebos_hit.webp',
    ss_erebos_down:      'assets/images/monsters/erebos_down.webp',
    ss_erebos_transform: 'assets/images/monsters/erebos_transform.webp',
    ss_erebos_form2_idle:    'assets/images/monsters/erebos2_idle.webp',
    ss_erebos_form2_attack:  'assets/images/monsters/erebos2_attack.webp',
    ss_erebos_form2_special: 'assets/images/monsters/erebos2_special.webp',
    ss_erebos_form2_move_f:  'assets/images/monsters/erebos2_move_f.webp',
    ss_erebos_form2_move_b:  'assets/images/monsters/erebos2_move_b.webp',
    ss_erebos_form2_dodge:   'assets/images/monsters/erebos2_dodge.webp',
    ss_erebos_form2_hit:     'assets/images/monsters/erebos2_hit.webp',
    ss_erebos_form2_down:    'assets/images/monsters/erebos2_down.webp',
    ss_alice_idle:   'assets/images/monsters/alice_idle.webp',
    ss_alice_attack: 'assets/images/monsters/alice_attack.webp',
    ss_alice_move_f: 'assets/images/monsters/alice_move_f.webp',
    ss_alice_move_b: 'assets/images/monsters/alice_move_b.webp',
    ss_alice_dodge:  'assets/images/monsters/alice_dodge.webp',
    ss_alice_hit:    'assets/images/monsters/alice_hit.webp',
    ss_alice_down:   'assets/images/monsters/alice_down.webp',
    ss_mococo_idle:   'assets/images/monsters/mococo_idle.webp',
    ss_mococo_attack: 'assets/images/monsters/mococo_attack.webp',
    ss_mococo_move_f: 'assets/images/monsters/mococo_move_f.webp',
    ss_mococo_move_b: 'assets/images/monsters/mococo_move_b.webp',
    ss_mococo_dodge:  'assets/images/monsters/mococo_dodge.webp',
    ss_mococo_hit:    'assets/images/monsters/mococo_hit.webp',
    ss_mococo_down:   'assets/images/monsters/mococo_down.webp',
  };

  private createCharacterDisplays(): void {
    const { characterY } = BATTLE_LAYOUT;
    const positions = DISTANCE_CHARACTER_POSITIONS[this.currentDistance];

    // モンスタースプライト画像（テクスチャが存在する場合のみ）
    const playerTextureKey = getMonsterBattleKey(this.playerMonster.id);
    const enemyTextureKey = getMonsterBattleKey(this.enemyMonster.id);

    this.playerCharacterImage = this.createCharacterObject(
      this.playerMonster.id, positions.playerX, characterY - 20, playerTextureKey, true  // 左向き画像を右向きに反転
    );
    this.enemyCharacterImage = this.createCharacterObject(
      this.enemyMonster.id, positions.enemyX, characterY - 20, enemyTextureKey, false  // 左向きのまま
    );

    // 名前テキスト（どちらかでも画像があれば下に配置）
    const hasAnyImage = this.playerCharacterImage || this.enemyCharacterImage;
    const spriteHalf = BATTLE_LAYOUT.characterDisplaySize / 2;
    const textY = hasAnyImage ? (characterY - 20) + spriteHalf + 10 : characterY;

    this.playerCharacterText = this.add
      .text(positions.playerX, textY, this.playerMonster.name, {
        fontSize: '28px',
        color: '#88ccff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 5,
      })
      .setOrigin(0.5);

    this.enemyCharacterText = this.add
      .text(positions.enemyX, textY, this.enemyMonster.name, {
        fontSize: '28px',
        color: '#ff8888',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 5,
      })
      .setOrigin(0.5);

    // 距離矢印
    const arrowY = hasAnyImage ? (characterY - 20) + spriteHalf + 30 : characterY + 60;
    this.add
      .text(GAME_WIDTH / 2, arrowY, '← 距離 →', {
        fontSize: '14px',
        color: '#cccccc',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5);
  }

  private createCharacterObject(
    monsterId: string,
    x: number,
    y: number,
    fallbackKey: string,
    flipX: boolean
  ): Phaser.GameObjects.Sprite | Phaser.GameObjects.Image | undefined {
    const sheetDef = BattleScene.SPRITESHEET_ANIMS[monsterId];

    if (sheetDef && this.textures.exists(sheetDef.idleKey)) {
      // 全シートのアニメーションを登録
      for (const sheet of sheetDef.sheets) {
        if (!this.textures.exists(sheet.key)) continue;
        for (const anim of sheet.anims) {
          if (!this.anims.exists(anim.key)) {
            this.anims.create({
              key: anim.key,
              frames: this.anims.generateFrameNumbers(sheet.key, { frames: anim.frames }),
              frameRate: anim.frameRate,
              repeat: anim.repeat,
            });
          }
        }
      }
      const baseSize = BATTLE_LAYOUT.characterDisplaySize;
      const size = isBossMonster(monsterId) ? Math.round(baseSize * BOSS_DISPLAY_SCALE) : baseSize;
      const sprite = this.add.sprite(x, y, sheetDef.idleKey, 0)
        .setOrigin(0.5)
        .setDisplaySize(size, size)
        .setFlipX(flipX);
      sprite.play(`${monsterId}_idle`);
      return sprite;
    }

    if (this.textures.exists(fallbackKey)) {
      const baseSize = BATTLE_LAYOUT.characterDisplaySize;
      const size = isBossMonster(monsterId) ? Math.round(baseSize * BOSS_DISPLAY_SCALE) : baseSize;
      return this.add.image(x, y, fallbackKey)
        .setOrigin(0.5)
        .setDisplaySize(size, size);
    }

    return undefined;
  }

  private createStatusDisplays(): void {
    const { statusY } = BATTLE_LAYOUT;

    // 距離表示
    this.distanceText = this.add
      .text(GAME_WIDTH / 2, statusY, '', {
        fontSize: '20px',
        color: BATTLE_COLORS.distanceText,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5);

    // 残り時間（画面上部中央）
    this.timeText = this.add
      .text(GAME_WIDTH / 2, BATTLE_LAYOUT.timeY, '', {
        fontSize: '24px',
        color: BATTLE_COLORS.timeText,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5);

    // プレイヤースタンス
    this.playerStanceText = this.add
      .text(BATTLE_LAYOUT.playerHpBarX, statusY, '', {
        fontSize: '16px',
        color: BATTLE_COLORS.playerStanceText,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0, 0.5);

    // 敵スタンス
    this.enemyStanceText = this.add
      .text(GAME_WIDTH - BATTLE_LAYOUT.playerHpBarX, statusY, '', {
        fontSize: '16px',
        color: BATTLE_COLORS.enemyStanceText,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(1, 0.5);

    // 初期値で表示更新
    this.updateDistanceDisplay();
    this.updateTimeDisplay();
    this.updateStanceDisplay();
  }

  private createCommandUI(): void {
    const deviceInfo = detectDevice();
    const layout = getCommandButtonLayout(deviceInfo.isMobile);
    const { row1Y, row2Y, buttonWidth, buttonHeight, buttonSpacing, row1StartX, row2StartX } = layout;
    const stanceLabels = this.commandManager.getStanceLabels();

    // コマンドボタン生成
    COMMAND_BUTTON_ROWS.forEach((row, rowIndex) => {
      const y = rowIndex === 0 ? row1Y : row2Y;
      const startX = rowIndex === 0 ? row1StartX : row2StartX;

      row.forEach((commandType, colIndex) => {
        const x = startX + colIndex * buttonSpacing;
        let label = COMMAND_LABELS[commandType];
        if (commandType === CommandType.STANCE_A) label = stanceLabels.stanceA;
        if (commandType === CommandType.STANCE_B) label = stanceLabels.stanceB;

        const bg = this.add
          .image(x, y, 'btn_command')
          .setDisplaySize(buttonWidth, buttonHeight)
          .setInteractive({ useHandCursor: true })
          .on('pointerdown', () => {
            bg.setAlpha(0.7);
            this.onCommandClick(commandType);
          })
          .on('pointerup', () => { this.updateCommandUI(); })
          .on('pointerout', () => { this.updateCommandUI(); });

        const text = this.add
          .text(x, y, label, {
            fontSize: '14px',
            color: COMMAND_UI_COLORS.buttonTextActive,
            fontFamily: "'Noto Sans JP', Arial, sans-serif",
            fontStyle: 'bold',
          })
          .setOrigin(0.5);

        this.commandButtons.set(commandType, { bg, text });
      });
    });

    // キャンセルボタン
    this.cancelButtonBg = this.add
      .rectangle(
        layout.cancelX,
        layout.cancelY,
        buttonWidth,
        buttonHeight,
        COMMAND_UI_COLORS.cancelButton
      )
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        this.cancelButtonBg.setFillStyle(COMMAND_UI_COLORS.cancelPressed);
        this.onCancelClick();
      })
      .on('pointerup', () => { this.cancelButtonBg.setFillStyle(COMMAND_UI_COLORS.cancelButton); })
      .on('pointerout', () => { this.cancelButtonBg.setFillStyle(COMMAND_UI_COLORS.cancelButton); });

    this.cancelButtonText = this.add
      .text(layout.cancelX, layout.cancelY, '戻す', {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    // 選択表示
    this.selectionText1st = this.add
      .text(240, layout.selectionY + 2, '1st: ---', {
        fontSize: '20px',
        color: COMMAND_UI_COLORS.selectionText,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(0.5);

    this.selectionText2nd = this.add
      .text(456, layout.selectionY + 2, '2nd: ---', {
        fontSize: '20px',
        color: COMMAND_UI_COLORS.selectionText,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(0.5);

    // 決定ボタン（幅はbuttonWidthを基準に計算、モバイルでも十分な大きさを確保）
    const confirmWidth = Math.round(buttonWidth * 1.3);
    this.confirmButtonBg = this.add
      .rectangle(696, layout.confirmY, confirmWidth, buttonHeight, COMMAND_UI_COLORS.confirmDisabled)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        this.confirmButtonBg.setFillStyle(COMMAND_UI_COLORS.confirmPressed);
        this.onConfirmClick();
      })
      .on('pointerup', () => { this.updateCommandUI(); })
      .on('pointerout', () => { this.updateCommandUI(); });

    this.confirmButtonText = this.add
      .text(696, layout.confirmY, '決定', {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    // 初期状態でUI更新
    this.updateCommandUI();
  }

  private onCommandClick(command: CommandType): void {
    if (this.isPlayingEffects) return;
    if (this.isWaitingForOpponent) return;
    // チュートリアル固定ターンではコマンド変更不可
    if (this.tutorialManager.isFixedTurn(this.battleState.currentTurn)) return;

    const success = this.commandManager.selectCommand(command);
    if (success) {
      playSe(this.sound, AudioKey.SE_SELECT);
      this.updateCommandUI();
    }
  }

  private onCancelClick(): void {
    if (this.isPlayingEffects) return;
    if (this.isWaitingForOpponent) return;
    // チュートリアル固定ターンではキャンセル不可
    if (this.tutorialManager.isFixedTurn(this.battleState.currentTurn)) return;

    playSe(this.sound, AudioKey.SE_SELECT);
    this.commandManager.cancelSelection();
    this.updateCommandUI();
  }

  private onConfirmClick(): void {
    if (this.isPlayingEffects) return;
    if (this.isWaitingForOpponent) return;
    if (this.isConfirmProcessing) return;

    const playerCommands = this.commandManager.confirmSelection();
    if (!playerCommands) return;

    this.isConfirmProcessing = true;

    // ネットワークモード: サーバーにコマンド送信
    if (this.isNetworkMode && this.socketClient && this.roomId) {
      this.socketClient.submitCommands(this.roomId, playerCommands);
      this.isWaitingForOpponent = true;
      this.setCommandUIEnabled(false);
      this.showWaitingMessage('相手のコマンド選択を待っています...');
      return;
    }

    // ローカルモード: CPU対戦処理
    this.processLocalTurn(playerCommands);
  }

  private processLocalTurn(playerCommands: TurnCommands): void {
    const currentTurn = this.battleState.currentTurn;
    const tutorialEnemyCmds = this.tutorialManager.getEnemyCommands(currentTurn);

    // ボスAI（非同期Lv6 + HPフェーズ別コマンドモディファイア）
    if (!tutorialEnemyCmds && this.isBossStage && isBossMonster(this.enemyMonster.id)) {
      const hpRatio = this.enemyCurrentHp / this.enemyMonster.stats.hp;
      const bossSpecificMods = getBossCommandMods(hpRatio);
      selectLv6Async(
        this.battleState, 'player2', this.enemyMonster, this.playerMonster,
        this.turnHistory, EREBOS_AI_CONFIG,
        { bossSpecificMods }
      ).then((enemyCommands) => {
        this.executeTurnWithCommands(playerCommands, enemyCommands);
      }).catch(() => {
        const fallback = selectCommandsForBattle(this.battleState, 'player2', this.enemyMonster, AILevel.LV5, this.playerMonster, this.turnHistory);
        this.executeTurnWithCommands(playerCommands, fallback);
      });
      return;
    }

    const enemyCommands = tutorialEnemyCmds ?? selectCommandsForBattle(
      this.battleState,
      'player2',
      this.enemyMonster,
      this.enemyAILevel,
      this.playerMonster,
      this.turnHistory,
    );
    this.executeTurnWithCommands(playerCommands, enemyCommands);
  }

  private executeTurnWithCommands(playerCommands: TurnCommands, enemyCommands: TurnCommands): void {

    // ターン処理
    const distanceBefore = this.battleState.currentDistance;
    const { newState, turnResult } = processTurn(
      this.battleState,
      this.playerMonster,
      this.enemyMonster,
      playerCommands,
      enemyCommands
    );

    // エフェクト解決
    const effectSequence = resolveBattleEffects(turnResult, distanceBefore);

    // エフェクト再生中はコマンド入力を無効化
    this.isPlayingEffects = true;
    this.setCommandUIEnabled(false);

    // フェーズ1後にHP表示を中間値に更新するコールバック
    const startPlayerHp = this.playerCurrentHp;
    const startEnemyHp = this.enemyCurrentHp;
    const onPhaseComplete = (phaseIndex: number) => {
      if (phaseIndex === 0 && !turnResult.phases[1].isSkipped) {
        const p1HpAfterPhase1 = Math.max(0, startPlayerHp - turnResult.phases[0].player1Damage.damage);
        const p2HpAfterPhase1 = Math.max(0, startEnemyHp - turnResult.phases[0].player2Damage.damage);
        this.updateHp(p1HpAfterPhase1, p2HpAfterPhase1);
      }
    };

    // エフェクト再生 → UI更新
    this.effectPlayer.playSequence(effectSequence, onPhaseComplete).then(() => {
      this.applyTurnResult(newState, turnResult);
    }).catch((error) => {
      console.error('Effect playback error:', error);
      this.applyTurnResult(newState, turnResult);
    });
  }

  /**
   * ターン結果をUIに反映し、次のターンの準備をする
   */
  private applyTurnResult(newState: BattleState, turnResult: TurnResult): void {
    this.battleState = newState;
    this.turnHistory.push(turnResult);

    // UI更新（シーン遷移中にテクスチャが破棄されている場合があるため例外を握りつぶす）
    try {
      this.updateHp(newState.player1.currentHp, newState.player2.currentHp);
      this.updateDistance(newState.currentDistance);
      this.updateStance(turnResult.player1StanceAfter, turnResult.player2StanceAfter);
      this.updateAbilityIndicators();
    } catch {
      // シーン破棄後のUI更新エラーは無視して処理を継続
    }

    // 勝敗判定（processTurnで更新済みのbattleStateを使用）
    const battleResult = checkVictoryAfterTurn(this.battleState);
    if (battleResult) {
      this.isPlayingEffects = false;
      battleResult.turnHistory = this.turnHistory;
      this.setCommandUIEnabled(false);
      this.spectatorAutoTurnTimer?.remove();
      logBattleEnd(this.gameMode ?? 'unknown', battleResult.resultType, this.battleState.currentTurn);
      this.transitionToResultWithDown(battleResult, {
        battleResult,
        mode: this.gameMode,
        stageNumber: this.stageNumber,
        clearedStages: this.clearedStages,
        monsterId: this.playerMonster.id,
        isReplay: this.isReplay,
      });
      return;
    }

    this.commandManager = new CommandSelectionManager(
      this.battleState,
      'player1',
      this.playerMonster
    );

    this.isPlayingEffects = false;
    this.isConfirmProcessing = false;

    if (this.isSpectatorMode) {
      // 観戦モード: 次のターンを自動実行
      this.scheduleSpectatorTurn();
    } else if (this.isAutoPlayerMode) {
      // AIオートモード: 次のターンをAIで自動実行
      this.scheduleAutoPlayerTurn();
    } else {
      this.setCommandUIEnabled(true);
      this.updateCommandUI();
      // 次のターンのチュートリアル処理
      this.startTutorialTurnIfNeeded(this.battleState.currentTurn);
    }
  }

  /**
   * コマンドUIの有効/無効を切り替える
   */
  private setCommandUIEnabled(enabled: boolean): void {
    // 観戦モード / AIオートモードではコマンドUIが存在しないためスキップ
    if (this.isSpectatorMode || this.isAutoPlayerMode) return;

    // UI無効化時にチュートリアルハイライトもクリア
    if (!enabled) {
      this.clearTutorialHighlights();
    }

    this.commandButtons.forEach((button) => {
      if (enabled) {
        button.bg.setInteractive({ useHandCursor: true });
      } else {
        button.bg.disableInteractive();
      }
    });
    if (enabled) {
      this.confirmButtonBg.setInteractive({ useHandCursor: true });
      this.cancelButtonBg.setInteractive({ useHandCursor: true });
    } else {
      this.confirmButtonBg.disableInteractive();
      this.cancelButtonBg.disableInteractive();
    }

    this.isCommandInputActive = enabled;

    // ネットワークモードではコマンド入力可能時にカウントダウンを表示
    if (this.isNetworkMode) {
      if (enabled) {
        this.startCommandCountdown();
      } else {
        this.stopCommandCountdown();
      }
    }
  }

  /**
   * コマンド入力カウントダウンを開始する（ネットワークモード用）
   */
  private startCommandCountdown(): void {
    this.stopCommandCountdown();
    this.commandCountdownSeconds = COMMAND_COUNTDOWN_SECONDS;

    if (!this.commandCountdownText) {
      this.commandCountdownText = this.add
        .text(
          GAME_WIDTH / 2,
          BATTLE_LAYOUT.countdownY,
          `残り ${COMMAND_COUNTDOWN_SECONDS}秒`,
          {
            fontSize: '20px',
            color: '#ffdd44',
            fontFamily: "'Noto Sans JP', Arial, sans-serif",
            stroke: '#000000',
            strokeThickness: 4,
          }
        )
        .setOrigin(0.5)
        .setDepth(10);
    }
    this.commandCountdownText.setText(`残り ${COMMAND_COUNTDOWN_SECONDS}秒`).setVisible(true);

    this.commandTimeoutTimer = this.time.addEvent({
      delay: 1000,
      repeat: COMMAND_COUNTDOWN_SECONDS - 1,
      callback: () => {
        this.commandCountdownSeconds--;
        const color =
          this.commandCountdownSeconds <= COMMAND_COUNTDOWN_WARNING_THRESHOLD
            ? '#ff4444'
            : '#ffdd44';
        this.commandCountdownText
          ?.setText(`残り ${this.commandCountdownSeconds}秒`)
          .setStyle({ color });
      },
    });
  }

  /**
   * コマンド入力カウントダウンを停止・非表示にする
   */
  private stopCommandCountdown(): void {
    this.commandTimeoutTimer?.remove();
    this.commandTimeoutTimer = undefined;
    this.commandCountdownText?.setVisible(false);
  }

  private updateCommandUI(): void {
    const selection = this.commandManager.getSelection();
    const validCommands = this.commandManager.getValidCommands();
    const stanceLabels = this.commandManager.getStanceLabels();

    // ボタンの有効/無効/選択状態を更新
    this.commandButtons.forEach((button, commandType) => {
      const isValid = validCommands.includes(commandType);
      const isSelected =
        selection.first === commandType || selection.second === commandType;

      if (!isValid) {
        button.bg.setTexture('btn_command').setAlpha(0.4);
        button.text.setColor(COMMAND_UI_COLORS.buttonTextDisabled);
        button.bg.disableInteractive();
      } else if (isSelected) {
        button.bg.setTexture('btn_command_active').setAlpha(1);
        button.text.setColor(COMMAND_UI_COLORS.buttonTextActive);
        button.bg.setInteractive({ useHandCursor: true });
      } else {
        button.bg.setTexture('btn_command').setAlpha(1);
        button.text.setColor(COMMAND_UI_COLORS.buttonTextActive);
        button.bg.setInteractive({ useHandCursor: true });
      }

      // スタンスボタンのラベルを動的に更新
      if (commandType === CommandType.STANCE_A) {
        button.text.setText(stanceLabels.stanceA);
      } else if (commandType === CommandType.STANCE_B) {
        button.text.setText(stanceLabels.stanceB);
      }
    });

    // 選択表示を更新
    const firstLabel = selection.first ? COMMAND_LABELS[selection.first] : '---';
    const secondLabel = selection.second ? COMMAND_LABELS[selection.second] : '---';
    this.selectionText1st.setText(`1st: ${firstLabel}`);
    this.selectionText2nd.setText(`2nd: ${secondLabel}`);

    // 決定ボタンの状態
    if (this.commandManager.canConfirm()) {
      this.confirmButtonBg.setFillStyle(COMMAND_UI_COLORS.confirmActive);
    } else {
      this.confirmButtonBg.setFillStyle(COMMAND_UI_COLORS.confirmDisabled);
    }
  }

  // --- 表示更新メソッド（内部用） ---

  private updateHpDisplay(): void {
    const { hpBarWidth } = BATTLE_LAYOUT;

    const playerRatio = this.playerCurrentHp / this.playerMonster.stats.hp;
    this.playerHpBarFill.setScale(playerRatio, 1);
    this.playerHpBarFill.x =
      BATTLE_LAYOUT.playerHpBarX + (hpBarWidth * playerRatio) / 2;
    this.playerHpText.setText(
      `${this.playerCurrentHp} / ${this.playerMonster.stats.hp}`
    );

    const enemyRatio = this.enemyCurrentHp / this.enemyMonster.stats.hp;
    this.enemyHpBarFill.setScale(enemyRatio, 1);
    this.enemyHpBarFill.x =
      BATTLE_LAYOUT.enemyHpBarX + hpBarWidth - (hpBarWidth * enemyRatio) / 2;
    this.enemyHpText.setText(
      `${this.enemyCurrentHp} / ${this.enemyMonster.stats.hp}`
    );
  }

  private updateDistanceDisplay(): void {
    this.distanceText.setText(`距離: ${DISTANCE_LABELS[this.currentDistance]}`);

    const positions = DISTANCE_CHARACTER_POSITIONS[this.currentDistance];
    this.playerCharacterText.x = positions.playerX;
    this.enemyCharacterText.x = positions.enemyX;

    if (this.playerCharacterImage) {
      this.playerCharacterImage.x = positions.playerX;
    }
    if (this.enemyCharacterImage) {
      this.enemyCharacterImage.x = positions.enemyX;
    }
  }

  private updateTimeDisplay(): void {
    this.timeText.setText(this.isBossStage ? '' : formatTime(this.remainingTime));
  }

  private updateStanceDisplay(): void {
    this.playerStanceText.setText(
      `スタンス: ${STANCE_LABELS[this.currentPlayerStance]}`
    );
    this.enemyStanceText.setText(
      `スタンス: ${STANCE_LABELS[this.currentEnemyStance]}`
    );
  }

  /**
   * 能力残回数インジケーターを生成する（コマンドボタンRow1の上に配置）
   */
  private createAbilityIndicators(): void {
    const { y: baseY, lineGap, fontSize } = INDICATOR_LAYOUT;
    // プレイヤーHPバーの中央X（左側に固定）
    const centerX = BATTLE_LAYOUT.playerHpBarX + BATTLE_LAYOUT.hpBarWidth / 2;

    // 特殊攻撃インジケーター（filled部分）
    this.specialIndicatorFilledText = this.add
      .text(centerX, baseY, '', {
        fontSize,
        color: INDICATOR_COLORS.specialNormal,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(1, 0.5);

    // 特殊攻撃インジケーター（empty部分）
    this.specialIndicatorEmptyText = this.add
      .text(centerX, baseY, '', {
        fontSize,
        color: INDICATOR_COLORS.specialNormal,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(0, 0.5);

    // リフレクターインジケーター（filled部分）
    this.reflectorIndicatorFilledText = this.add
      .text(centerX, baseY + lineGap, '', {
        fontSize,
        color: INDICATOR_COLORS.reflectorNormal,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(1, 0.5);

    // リフレクターインジケーター（empty部分）
    this.reflectorIndicatorEmptyText = this.add
      .text(centerX, baseY + lineGap, '', {
        fontSize,
        color: INDICATOR_COLORS.reflectorNormal,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(0, 0.5);

    this.updateAbilityIndicators();
  }

  /**
   * 能力残回数インジケーターの表示を更新する
   * 自分のプレイヤー側のみ表示（相手は非表示）
   */
  private updateAbilityIndicators(): void {
    if (!this.specialIndicatorFilledText) return;
    if (!this.scene.isActive()) return;

    const playerSide = this.isNetworkMode && this.playerNumber === 2 ? 'player2' : 'player1';
    const playerState = this.battleState[playerSide];

    const maxSpecial = this.playerMonster.stats.specialAttackCount;
    const remainingSpecial = playerState.remainingSpecialCount;
    const maxReflect = this.playerMonster.reflector.maxReflectCount;
    const remainingReflect = maxReflect - playerState.usedReflectCount;

    const indicatorCenterX = BATTLE_LAYOUT.playerHpBarX + BATTLE_LAYOUT.hpBarWidth / 2;

    // 特殊攻撃インジケーター更新
    this.updateIndicatorPair(
      this.specialIndicatorFilledText!,
      this.specialIndicatorEmptyText!,
      remainingSpecial,
      maxSpecial,
      INDICATOR_CHARS.specialFilled,
      INDICATOR_CHARS.specialEmpty,
      getSpecialIndicatorColor(remainingSpecial, maxSpecial),
      indicatorCenterX,
    );

    // リフレクターインジケーター更新
    this.updateIndicatorPair(
      this.reflectorIndicatorFilledText!,
      this.reflectorIndicatorEmptyText!,
      remainingReflect,
      maxReflect,
      INDICATOR_CHARS.reflectorFilled,
      INDICATOR_CHARS.reflectorEmpty,
      getReflectorIndicatorColor(remainingReflect, maxReflect),
      indicatorCenterX,
    );
  }

  /**
   * filled/empty のテキストペアを更新する（共通ロジック）
   */
  private updateIndicatorPair(
    filledText: Phaser.GameObjects.Text,
    emptyText: Phaser.GameObjects.Text,
    remaining: number,
    max: number,
    filledChar: string,
    emptyChar: string,
    color: string,
    centerX: number,
  ): void {
    const clamped = Math.max(0, remaining);

    if (clamped <= 0) {
      // 全消費/超過: 全ドットを同一色で表示
      filledText.setText(emptyChar.repeat(max));
      filledText.setColor(color);
      filledText.setOrigin(0.5, 0.5);
      filledText.x = centerX;
      emptyText.setText('');
    } else {
      // 通常: filled部分とempty部分を異なる色で横に並べる
      const filledStr = filledChar.repeat(clamped);
      const emptyStr = emptyChar.repeat(max - clamped);
      filledText.setText(filledStr);
      filledText.setColor(color);
      emptyText.setText(emptyStr);
      emptyText.setColor('#444466');

      // 中央揃えで配置
      const filledWidth = filledText.width;
      const totalWidth = filledWidth + emptyText.width;
      const startX = centerX - totalWidth / 2;
      filledText.setOrigin(0, 0.5);
      filledText.x = startX;
      emptyText.x = startX + filledWidth;
    }
  }

  // --- 公開メソッド（後続issueから呼び出し可能） ---

  /** HP更新 */
  public updateHp(playerHp: number, enemyHp: number): void {
    this.playerCurrentHp = clampHp(playerHp, this.playerMonster.stats.hp);
    this.enemyCurrentHp = clampHp(enemyHp, this.enemyMonster.stats.hp);
    this.updateHpDisplay();
  }

  /** 距離更新 */
  public updateDistance(distance: DistanceType): void {
    this.currentDistance = distance;
    this.updateDistanceDisplay();
  }

  /** スタンス更新 */
  public updateStance(playerStance: StanceType, enemyStance: StanceType): void {
    this.currentPlayerStance = playerStance;
    this.currentEnemyStance = enemyStance;
    this.updateStanceDisplay();
  }

  /** 残り時間更新 */
  public updateTime(time: number): void {
    this.remainingTime = Math.max(0, time);
    this.updateTimeDisplay();
  }

  // --- ネットワークモード ---

  private setupNetworkListeners(): void {
    if (!this.socketClient) return;

    this.socketClient.updateCallbacks({
      onWaitingForCommands: () => {
        this.setCommandUIEnabled(true);
        this.updateCommandUI();
      },
      onTurnResult: (payload) => this.handleNetworkTurnResult(payload),
      onBattleFinished: (payload) => this.handleNetworkBattleFinished(payload),
      onOpponentDisconnected: () => this.handleOpponentDisconnected(),
      onCommandTimeout: (payload) => this.handleCommandTimeout(payload),
      onDisconnect: () => this.handleOwnDisconnect(),
    });
  }

  /**
   * Player2視点用にTurnResultのplayer1/player2を入れ替える
   */
  private swapTurnResultPerspective(turnResult: TurnResult): TurnResult {
    return {
      ...turnResult,
      player1Commands: turnResult.player2Commands,
      player2Commands: turnResult.player1Commands,
      player1Damage: turnResult.player2Damage,
      player2Damage: turnResult.player1Damage,
      player1StanceAfter: turnResult.player2StanceAfter,
      player2StanceAfter: turnResult.player1StanceAfter,
      phases: turnResult.phases.map(phase => ({
        ...phase,
        player1Command: phase.player2Command,
        player2Command: phase.player1Command,
        player1Damage: phase.player2Damage,
        player2Damage: phase.player1Damage,
      })) as [typeof turnResult.phases[0], typeof turnResult.phases[1]],
    };
  }

  private handleNetworkTurnResult(payload: import('../../shared/types/SocketEvents').TurnResultPayload): void {
    if (payload.roomId !== this.roomId) return;

    this.isWaitingForOpponent = false;
    this.hideWaitingMessage();

    const { turnResult: rawTurnResult, newState } = payload;

    // Player2視点の場合、エフェクト表示のためにplayer1/player2を入れ替え
    const turnResult = this.playerNumber === 2
      ? this.swapTurnResultPerspective(rawTurnResult)
      : rawTurnResult;

    // エフェクト解決
    const distanceBefore = this.battleState.currentDistance;
    const effectSequence = resolveBattleEffects(turnResult, distanceBefore);

    // サーバーの残り時間をエフェクト開始前に同期（ターン終了時のジャンプを防ぐ）
    this.updateTime(newState.remainingTime);

    // エフェクト再生中はコマンド入力を無効化
    this.isPlayingEffects = true;
    this.setCommandUIEnabled(false);

    // フェーズ1後にHP表示を中間値に更新するコールバック
    const startPlayerHp = this.playerCurrentHp;
    const startEnemyHp = this.enemyCurrentHp;
    const onPhaseComplete = (phaseIndex: number) => {
      if (phaseIndex === 0 && !turnResult.phases[1].isSkipped) {
        const p1HpAfterPhase1 = Math.max(0, startPlayerHp - turnResult.phases[0].player1Damage.damage);
        const p2HpAfterPhase1 = Math.max(0, startEnemyHp - turnResult.phases[0].player2Damage.damage);
        this.updateHp(p1HpAfterPhase1, p2HpAfterPhase1);
      }
    };

    // エフェクト再生 → UI更新
    this.effectPlayer.playSequence(effectSequence, onPhaseComplete).then(() => {
      this.applyNetworkTurnResult(newState, turnResult);
    }).catch((error) => {
      console.error('Effect playback error:', error);
      this.applyNetworkTurnResult(newState, turnResult);
    });
  }

  private applyNetworkTurnResult(newState: BattleState, turnResult: TurnResult): void {
    this.battleState = newState;
    this.turnHistory.push(turnResult);

    // Player視点でHP/スタンスを更新
    if (this.playerNumber === 2) {
      this.updateHp(newState.player2.currentHp, newState.player1.currentHp);
      this.updateStance(turnResult.player2StanceAfter, turnResult.player1StanceAfter);
    } else {
      this.updateHp(newState.player1.currentHp, newState.player2.currentHp);
      this.updateStance(turnResult.player1StanceAfter, turnResult.player2StanceAfter);
    }
    this.updateDistance(newState.currentDistance);
    this.updateAbilityIndicators();

    // エフェクト終了をサーバーに通知（残り時間計測はエフェクト時間のみにするため）
    if (this.socketClient && this.roomId) {
      this.socketClient.effectsDone(this.roomId);
    }

    this.isPlayingEffects = false;
    this.isConfirmProcessing = false;

    if (!newState.isFinished) {
      const playerSide = this.playerNumber === 2 ? 'player2' : 'player1';
      this.commandManager = new CommandSelectionManager(
        this.battleState,
        playerSide,
        this.playerMonster
      );

      this.setCommandUIEnabled(true);
      this.updateCommandUI();
    }

    // エフェクト完了後に保留していた BATTLE_FINISHED を処理する
    if (this.pendingBattleFinishedPayload) {
      const payload = this.pendingBattleFinishedPayload;
      this.pendingBattleFinishedPayload = undefined;
      this.setCommandUIEnabled(false);
      this.executeBattleFinishedTransition(payload);
    }
  }

  private handleNetworkBattleFinished(payload: import('../../shared/types/SocketEvents').BattleFinishedPayload): void {
    if (payload.roomId !== this.roomId) return;

    this.isWaitingForOpponent = false;
    this.hideWaitingMessage();

    // エフェクト再生中の場合、完了後に遷移する
    if (this.isPlayingEffects) {
      this.pendingBattleFinishedPayload = payload;
      return;
    }

    this.setCommandUIEnabled(false);
    this.executeBattleFinishedTransition(payload);
  }

  /**
   * battleResult から敗者側（player/enemy）を判定する
   * player1 = player, player2 = enemy（playerNumber=1視点）
   */
  /** CSS カラー文字列（#rrggbb）を Phaser 用 number（0xrrggbb）に変換 */
  private themeColorToHex(cssColor: string | undefined): number | undefined {
    if (!cssColor) return undefined;
    return parseInt(cssColor.replace('#', '0x'), 16);
  }

  private getLoserTarget(resultType: BattleResultType): 'player' | 'enemy' | null {
    if (resultType === BattleResultType.PLAYER1_WIN) return 'enemy';
    if (resultType === BattleResultType.PLAYER2_WIN) return 'player';
    return null; // DRAW
  }

  /**
   * ダウンアニメ再生 → リザルト画面遷移
   */
  private transitionToResultWithDown(
    battleResult: import('../types/BattleState').BattleResult,
    sceneData: Record<string, unknown>
  ): void {
    const loserTarget = this.getLoserTarget(battleResult.resultType);
    this.effectPlayer.playDownAnimation(loserTarget, () => {
      this.time.delayedCall(400, () => {
        if (this.gameMode === GameMode.SURVIVAL && this.survivalSaveData) {
          this.transitionToSurvivalNext(battleResult);
        } else if (this.isBossStage) {
          this.transitionToBossNext(battleResult);
        } else if (this.gameMode === GameMode.FREE_CPU_3V3) {
          this.transitionTo3v3Next(battleResult);
        } else if (this.isTournamentMode() && this.tournamentState !== undefined && this.tournamentMatchIndex !== undefined) {
          this.transitionToTournamentNext(battleResult);
        } else {
          const hiddenChallengeData = this.isHiddenChallenge
            ? { isHiddenChallenge: true, challengeTargetId: this.challengeTargetId }
            : {};
          const resultData = this.isNetworkMode
            ? { ...sceneData, ...hiddenChallengeData, socketClient: this.socketClient }
            : { ...sceneData, ...hiddenChallengeData };
          this.transitionTo(SceneKey.RESULT, resultData);
        }
      });
    });
  }

  /** ボス第1形態 登場カットイン */
  private showBossEntranceCutscene(): void {
    this.isPlayingEffects = true;
    this.setCommandUIEnabled(false);

    const depth = 300;
    const overlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85)
      .setDepth(depth).setAlpha(0);
    const portraitKey = `monster_${this.enemyMonster.id}_battle`;
    const portrait = this.textures.exists(portraitKey)
      ? this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, portraitKey)
          .setDisplaySize(GAME_HEIGHT, GAME_HEIGHT)
          .setDepth(depth + 1).setAlpha(0)
      : null;

    // フェードイン
    this.tweens.add({ targets: overlay, alpha: 1, duration: 300 });
    if (portrait) {
      this.tweens.add({ targets: portrait, alpha: 1, duration: 400, delay: 200,
        onComplete: () => {
          // 2秒表示後フェードアウト
          this.time.delayedCall(2000, () => {
            this.tweens.add({
              targets: [overlay, portrait], alpha: 0, duration: 500,
              onComplete: () => {
                overlay.destroy();
                portrait.destroy();
                this.isPlayingEffects = false;
                this.setCommandUIEnabled(true);
              },
            });
          });
        },
      });
    } else {
      this.time.delayedCall(800, () => {
        this.tweens.add({
          targets: overlay, alpha: 0, duration: 400,
          onComplete: () => {
            overlay.destroy();
            this.isPlayingEffects = false;
            this.setCommandUIEnabled(true);
          },
        });
      });
    }
  }

  /** ボス第2形態 変身カットイン */
  private showBossTransformCutscene(): void {
    this.isPlayingEffects = true;
    this.setCommandUIEnabled(false);

    const depth = 300;
    const TRANSFORM_KEY = 'ss_erebos_transform';
    const ANIM_KEY = 'erebos_transform_cutscene';

    const overlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.9)
      .setDepth(depth);

    if (this.textures.exists(TRANSFORM_KEY)) {
      if (!this.anims.exists(ANIM_KEY)) {
        this.anims.create({
          key: ANIM_KEY,
          frames: this.anims.generateFrameNumbers(TRANSFORM_KEY, { start: 0, end: 8 }),
          frameRate: 6,
          repeat: 0,
        });
      }
      const sprite = this.add.sprite(GAME_WIDTH / 2, GAME_HEIGHT / 2, TRANSFORM_KEY, 0)
        .setDisplaySize(GAME_HEIGHT, GAME_HEIGHT)
        .setDepth(depth + 1);
      sprite.play(ANIM_KEY);
      sprite.once('animationcomplete', () => {
        this.time.delayedCall(300, () => {
          this.tweens.add({
            targets: [overlay, sprite], alpha: 0, duration: 400,
            onComplete: () => {
              overlay.destroy();
              sprite.destroy();
              this.isPlayingEffects = false;
              this.setCommandUIEnabled(true);
              this.evictBossForm1Sprites();
            },
          });
        });
      });
    } else {
      this.time.delayedCall(600, () => {
        overlay.destroy();
        this.isPlayingEffects = false;
        this.setCommandUIEnabled(true);
        this.evictBossForm1Sprites();
      });
    }
  }

  /** シーン終了時、全バトルスプライトのアニメーションとテクスチャを解放する */
  private evictHiddenCharSprites(): void {
    for (const def of Object.values(BattleScene.SPRITESHEET_ANIMS)) {
      for (const sheet of def.sheets) {
        for (const anim of sheet.anims) {
          this.anims.remove(anim.key);
        }
        this.textures.remove(sheet.key);
      }
    }
    this.textures.remove('ss_erebos_transform');
  }

  /** 変身カットシーン完了後、form1 スプライトを GPU メモリから解放する */
  private evictBossForm1Sprites(): void {
    const form1Def = BattleScene.SPRITESHEET_ANIMS['erebos'];
    if (form1Def) {
      for (const sheet of form1Def.sheets) {
        this.textures.remove(sheet.key);
      }
    }
    this.textures.remove('ss_erebos_transform');
  }

  private transitionToBossNext(battleResult: BattleResult): void {
    const { playerDefeated, enemyDefeated } = determine3v3BattleOutcome(battleResult.resultType);
    const finalState = battleResult.finalState;

    let updatedPlayerParty = updatePartyAfterBattle(
      this.playerParty, this.playerPartyIndex, finalState.player1, playerDefeated
    );
    let updatedEnemyParty = updatePartyAfterBattle(
      this.enemyParty, this.enemyPartyIndex, finalState.player2, enemyDefeated
    );

    const nextPlayerIndex = playerDefeated
      ? getNextPartyIndex(updatedPlayerParty, this.playerPartyIndex)
      : this.playerPartyIndex;
    const nextEnemyIndex = enemyDefeated
      ? getNextPartyIndex(updatedEnemyParty, this.enemyPartyIndex)
      : this.enemyPartyIndex;

    // プレイヤーパーティ全滅 → 敗北
    if (isPartyDefeated(updatedPlayerParty) || nextPlayerIndex === null) {
      this.transitionTo(SceneKey.RESULT, {
        battleResult: { ...battleResult, resultType: BattleResultType.PLAYER2_WIN },
        mode: this.gameMode,
        stageNumber: this.stageNumber,
        clearedStages: this.clearedStages,
        monsterId: this.playerMonster.id,
        isBossStage: true,
      });
      return;
    }

    // ボスパーティ全滅 → CONGRATULATIONS
    if (isPartyDefeated(updatedEnemyParty) || nextEnemyIndex === null) {
      this.transitionTo(SceneKey.RESULT, {
        battleResult: { ...battleResult, resultType: BattleResultType.PLAYER1_WIN },
        mode: this.gameMode,
        stageNumber: this.stageNumber,
        clearedStages: this.clearedStages,
        monsterId: this.playerMonster.id,
        isBossStage: true,
        playerParty: updatedPlayerParty,
      });
      return;
    }

    // 第1形態撃破 → 第2形態へ（変身演出付き）、またはプレイヤー交代
    const isFormChange = enemyDefeated && nextEnemyIndex !== null;
    // プレイヤー交代時は現在の背景を引き継ぐ。形態変化時は第2形態用背景に切替
    const nextBgKey = isFormChange
      ? BackgroundImageKey.BATTLE_BOSS_2
      : (this.currentBattleBgKey ?? BackgroundImageKey.BATTLE_BOSS_1);
    this.transitionTo(SceneKey.BATTLE, {
      mode: GameMode.CHALLENGE,
      stageNumber: this.stageNumber,
      clearedStages: this.clearedStages,
      isBossStage: true,
      isFormChange,
      playerParty: updatedPlayerParty,
      enemyParty: updatedEnemyParty,
      playerPartyIndex: nextPlayerIndex,
      enemyPartyIndex: nextEnemyIndex,
      selectedBattleBg: nextBgKey,
    });
  }

  private transitionTo3v3Next(battleResult: BattleResult): void {
    const { playerDefeated, enemyDefeated } = determine3v3BattleOutcome(battleResult.resultType);

    // パーティ状態を更新（使用回数引き継ぎ）
    const finalState = battleResult.finalState;
    let updatedPlayerParty = updatePartyAfterBattle(
      this.playerParty,
      this.playerPartyIndex,
      finalState.player1,
      playerDefeated
    );
    let updatedEnemyParty = updatePartyAfterBattle(
      this.enemyParty,
      this.enemyPartyIndex,
      finalState.player2,
      enemyDefeated
    );

    // 次のメンバーのインデックスを決定
    const nextPlayerIndex = playerDefeated
      ? getNextPartyIndex(updatedPlayerParty, this.playerPartyIndex)
      : this.playerPartyIndex;
    const nextEnemyIndex = enemyDefeated
      ? getNextPartyIndex(updatedEnemyParty, this.enemyPartyIndex)
      : this.enemyPartyIndex;

    // どちらかのパーティが全滅したら結果画面へ
    if (isPartyDefeated(updatedPlayerParty) || isPartyDefeated(updatedEnemyParty) || nextPlayerIndex === null || nextEnemyIndex === null) {
      // 全体の勝敗: 自分のパーティが全滅していれば負け
      const overallResult = isPartyDefeated(updatedPlayerParty) && !isPartyDefeated(updatedEnemyParty)
        ? BattleResultType.PLAYER2_WIN
        : !isPartyDefeated(updatedPlayerParty) && isPartyDefeated(updatedEnemyParty)
          ? BattleResultType.PLAYER1_WIN
          : BattleResultType.DRAW;
      this.transitionTo(SceneKey.RESULT, {
        battleResult: { ...battleResult, resultType: overallResult },
        mode: this.gameMode,
        monsterId: this.playerMonster.id,
      });
      return;
    }

    // 次の対戦へ
    this.transitionTo(SceneKey.BATTLE, {
      mode: GameMode.FREE_CPU_3V3,
      aiLevel: this.enemyAILevel,
      playerParty: updatedPlayerParty,
      enemyParty: updatedEnemyParty,
      playerPartyIndex: nextPlayerIndex,
      enemyPartyIndex: nextEnemyIndex,
    });
  }

  private transitionToSurvivalNext(
    battleResult: BattleResult,
  ): void {
    const isPlayerWin = battleResult.resultType === BattleResultType.PLAYER1_WIN;
    if (isPlayerWin) {
      const updatedSave = recordSurvivalWin(this.survivalSaveData!);
      saveSurvivalData(updatedSave);
      this.transitionTo(SceneKey.SURVIVAL_EXP_ALLOCATION, { saveData: updatedSave });
    } else {
      this.transitionTo(SceneKey.SURVIVAL_RESULT, {
        winCount: this.survivalSaveData!.winCount,
        monsterId: this.survivalSaveData!.monsterId,
      });
    }
  }

  private isTournamentMode(): boolean {
    return (
      this.gameMode === GameMode.TOURNAMENT_ROUND_ROBIN ||
      this.gameMode === GameMode.TOURNAMENT_ELIMINATION ||
      (this.isSpectatorMode && this.tournamentState !== undefined)
    );
  }

  private transitionToTournamentNext(battleResult: BattleResult): void {
    const matchIndex = this.tournamentMatchIndex!;
    let state = this.tournamentState!;
    const match = state.matches[matchIndex];

    // 引き分けの場合: 最大3回まで再試合
    if (battleResult.resultType === BattleResultType.DRAW) {
      const MAX_DRAW_RETRIES = 3;
      const retryCount = this.tournamentDrawRetryCount + 1;

      if (retryCount <= MAX_DRAW_RETRIES) {
        this.showTournamentDrawRetryMessage(retryCount, () => {
          this.transitionTo(SceneKey.BATTLE, {
            monsterId: this.playerMonster.id,
            enemyMonsterId: this.enemyMonster.id,
            aiLevel: this.enemyAILevel,
            enemyAiLevel: this.isSpectatorMode ? this.enemyAILevel2 : undefined,
            mode: this.gameMode,
            tournamentState: state,
            tournamentMatchIndex: matchIndex,
            tournamentPlayerIsP1: this.tournamentPlayerIsP1,
            tournamentDrawRetryCount: retryCount,
            playerAIConfig: this.playerAIConfig,
            enemyAIConfig: this.enemyAIConfig,
          });
        });
        return;
      }

      // 再試合上限超過: participant1の勝利として扱う
      battleResult = { ...battleResult, resultType: BattleResultType.PLAYER1_WIN };
    }

    // BattleScene では player1=player, player2=enemy だが、
    // 大会では tournamentPlayerIsP1 で向きが変わる場合がある
    const isP1 = this.tournamentPlayerIsP1 ?? true;
    let winnerId: string | null = null;
    let loserId: string | null = null;

    if (battleResult.resultType === BattleResultType.PLAYER1_WIN) {
      // BattleScene の player1 が勝利
      winnerId = isP1 ? match.participant1Id : match.participant2Id;
      loserId = isP1 ? match.participant2Id : match.participant1Id;
    } else if (battleResult.resultType === BattleResultType.PLAYER2_WIN) {
      winnerId = isP1 ? match.participant2Id : match.participant1Id;
      loserId = isP1 ? match.participant1Id : match.participant2Id;
    }

    state = recordMatchResult(state, matchIndex, {
      winnerId,
      loserId,
      battleResult: battleResult.resultType,
    });

    if (state.mode === TournamentMode.SINGLE_ELIMINATION) {
      state = advanceEliminationBracket(state, matchIndex);
    }

    this.transitionTo(SceneKey.TOURNAMENT_BRACKET, { tournamentState: state });
  }

  private showTournamentDrawRetryMessage(retryCount: number, onDone: () => void): void {
    const depth = 200;
    const overlay = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7
    ).setDepth(depth);

    const line1 = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20, '引き分け！', {
      fontSize: '36px',
      color: '#ffdd44',
      fontFamily: "'Noto Sans JP', Arial, sans-serif",
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 6,
    }).setOrigin(0.5).setDepth(depth + 1);

    const line2 = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 30, `再試合します（${retryCount}回目）`, {
      fontSize: '22px',
      color: '#ffffff',
      fontFamily: "'Noto Sans JP', Arial, sans-serif",
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(depth + 1);

    this.time.delayedCall(2500, () => {
      overlay.destroy();
      line1.destroy();
      line2.destroy();
      onDone();
    });
  }

  private executeBattleFinishedTransition(payload: import('../../shared/types/SocketEvents').BattleFinishedPayload): void {
    if (this.hasNetworkBattleTransitioned) return;
    this.hasNetworkBattleTransitioned = true;
    const adjustedResult = buildPlayerBattleResult(payload.result, this.playerNumber ?? 1);
    this.transitionToResultWithDown(adjustedResult, {
      battleResult: adjustedResult,
      mode: this.gameMode,
      monsterId: this.playerMonster.id,
    });
  }

  private handleOpponentDisconnected(): void {
    this.isWaitingForOpponent = false;
    this.isConfirmProcessing = false;
    this.hideWaitingMessage();
    this.showWaitingMessage('相手が切断しました...');
    // BATTLE_FINISHED イベントで結果画面に遷移する
  }

  private handleOwnDisconnect(): void {
    if (this.hasNetworkBattleTransitioned) return;
    this.hasNetworkBattleTransitioned = true;
    this.setCommandUIEnabled(false);
    this.showWaitingMessage('接続が切れました。メニューに戻ります...');
    this.time.delayedCall(2000, () => {
      this.transitionTo(SceneKey.MODE_SELECT);
    });
  }

  private handleCommandTimeout(payload: import('../../shared/types/SocketEvents').CommandTimeoutPayload): void {
    if (payload.roomId !== this.roomId) return;
    // タイムアウト発生時はカウントダウンを非表示にする
    // その後サーバーが自動でコマンド提出し、TURN_RESULT が続けて送られる
    this.stopCommandCountdown();
  }

  private showWaitingMessage(message: string): void {
    this.hideWaitingMessage();
    this.waitingText = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2, message, {
        fontSize: '20px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        backgroundColor: '#000000aa',
        padding: { x: 20, y: 10 },
      })
      .setOrigin(0.5)
      .setDepth(50);
  }

  private hideWaitingMessage(): void {
    this.waitingText?.destroy();
    this.waitingText = undefined;
  }

  // --- チュートリアル ---

  /**
   * チュートリアルターン開始処理
   * ポップアップ表示 → 固定コマンド自動選択
   */
  private startTutorialTurnIfNeeded(turn: number): void {
    const message = this.tutorialManager.getPopupMessage(turn);
    if (!message) return;

    const detailMessage = this.tutorialManager.getDetailMessage(turn);
    const stepLabel = this.tutorialManager.getStepLabel(turn);

    // コマンドUI無効化（ポップアップ表示中）
    this.setCommandUIEnabled(false);

    this.showTutorialPopup(message, detailMessage, stepLabel, () => {
      // ターン6の自由戦闘メッセージ表示後はフラグ更新
      if (!this.tutorialManager.isFixedTurn(turn)) {
        this.tutorialManager.markFreeBattleMessageShown();
        this.setCommandUIEnabled(true);
        return;
      }

      // 固定コマンドを自動選択
      this.autoSelectTutorialCommands(turn);
      this.setCommandUIEnabled(true);
    });
  }

  /**
   * チュートリアルポップアップ表示
   */
  private showTutorialPopup(
    message: string,
    detailMessage: string | null,
    stepLabel: string | null,
    onDismiss: () => void,
  ): void {
    // 半透明オーバーレイ
    this.tutorialPopupOverlay = this.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setDepth(100)
      .setInteractive();

    // ステップラベル（例: "ステップ 1/5"）
    if (stepLabel) {
      this.tutorialPopupStepText = this.add
        .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 100, stepLabel, {
          fontSize: '14px',
          color: '#aaddff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
        })
        .setOrigin(0.5)
        .setDepth(101);
    }

    // タイトルメッセージ
    this.tutorialPopupText = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 70, message, {
        fontSize: '22px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        align: 'center',
        wordWrap: { width: GAME_WIDTH - 100 },
      })
      .setOrigin(0.5)
      .setDepth(101);

    // 詳細メッセージ
    if (detailMessage) {
      this.tutorialPopupDetailText = this.add
        .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 10, detailMessage, {
          fontSize: '16px',
          color: '#cccccc',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          align: 'center',
          lineSpacing: 6,
          wordWrap: { width: GAME_WIDTH - 120 },
        })
        .setOrigin(0.5)
        .setDepth(101);
    }

    // 「タップして続ける」ヒント
    this.tutorialPopupHint = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 110, 'タップして続ける', {
        fontSize: '14px',
        color: '#999999',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(101);

    // クリックで閉じる
    this.tutorialPopupOverlay.once('pointerdown', () => {
      this.dismissTutorialPopup();
      onDismiss();
    });
  }

  /**
   * チュートリアルポップアップを閉じる
   */
  private dismissTutorialPopup(): void {
    this.tutorialPopupOverlay?.destroy();
    this.tutorialPopupText?.destroy();
    this.tutorialPopupDetailText?.destroy();
    this.tutorialPopupStepText?.destroy();
    this.tutorialPopupHint?.destroy();
    this.tutorialPopupOverlay = undefined;
    this.tutorialPopupText = undefined;
    this.tutorialPopupDetailText = undefined;
    this.tutorialPopupStepText = undefined;
    this.tutorialPopupHint = undefined;
  }

  /**
   * チュートリアル固定コマンドを自動選択する
   */
  private autoSelectTutorialCommands(turn: number): void {
    const playerCmds = this.tutorialManager.getPlayerCommands(turn);
    if (!playerCmds) return;

    this.commandManager.selectCommand(playerCmds.first.type);
    this.commandManager.selectCommand(playerCmds.second.type);
    this.updateCommandUI();

    // 固定ターン: コマンドボタンとキャンセルを無効化し、決定のみ有効にする
    this.commandButtons.forEach((button) => {
      button.bg.disableInteractive();
    });
    this.cancelButtonBg.disableInteractive();

    // ハイライトアニメーション
    this.highlightTutorialButtons(turn);
  }

  /**
   * チュートリアル固定ターンで選択されたコマンドボタンとインジケーターをハイライト表示する
   */
  private highlightTutorialButtons(turn: number): void {
    this.clearTutorialHighlights();

    // コマンドボタンのハイライト
    const commandTypes = this.tutorialManager.getHighlightedCommands(turn);

    commandTypes.forEach((cmdType) => {
      const button = this.commandButtons.get(cmdType);
      if (!button) return;

      button.bg.setTint(COMMAND_UI_COLORS.tutorialHighlight);

      const tween = this.tweens.addCounter({
        from: 1,
        to: 0.3,
        duration: 600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        onUpdate: (t) => {
          const alpha = t.getValue() ?? 1;
          button.bg.setAlpha(alpha);
        },
      });

      this.tutorialHighlightTweens.push(tween);
    });

    // インジケーターのハイライト
    const indicators = this.tutorialManager.getHighlightedIndicators(turn);

    indicators.forEach((type) => {
      const targets: Phaser.GameObjects.Text[] = [];
      if (type === 'special') {
        if (this.specialIndicatorFilledText) targets.push(this.specialIndicatorFilledText);
        if (this.specialIndicatorEmptyText) targets.push(this.specialIndicatorEmptyText);
      } else if (type === 'reflector') {
        if (this.reflectorIndicatorFilledText) targets.push(this.reflectorIndicatorFilledText);
        if (this.reflectorIndicatorEmptyText) targets.push(this.reflectorIndicatorEmptyText);
      }

      targets.forEach((target) => {
        const tween = this.tweens.add({
          targets: target,
          scaleX: { from: 1, to: 1.3 },
          scaleY: { from: 1, to: 1.3 },
          duration: 600,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
        this.tutorialHighlightTweens.push(tween);
      });
    });
  }

  /**
   * チュートリアルハイライトをクリアする
   */
  private clearTutorialHighlights(): void {
    this.tutorialHighlightTweens.forEach((t) => t.destroy());
    this.tutorialHighlightTweens = [];

    this.commandButtons.forEach((button) => {
      button.bg.clearTint();
    });
    // アルファをボタン状態に合わせて復元
    this.updateCommandUI();

    // インジケーターのスケールをリセット
    [
      this.specialIndicatorFilledText,
      this.specialIndicatorEmptyText,
      this.reflectorIndicatorFilledText,
      this.reflectorIndicatorEmptyText,
    ].forEach((text) => {
      if (text) {
        text.setScale(1);
      }
    });
  }

  // --- 観戦モード ---

  /**
   * 観戦モード用UI（中断ボタン）を生成する。
   * コマンド選択UIは生成しない。
   */
  private createSpectatorUI(): void {
    const x = 890;
    const y = GAME_HEIGHT - 15;
    const width = 100;
    const height = 28;

    this.surrenderButtonBg = this.add
      .rectangle(x, y, width, height, 0x664444)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.onSpectatorInterrupt());

    this.surrenderButtonText = this.add
      .text(x, y, SPECTATOR_MESSAGES.interruptButtonLabel, {
        fontSize: '13px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5);
  }

  /**
   * 観戦モード: 自動ターンをスケジュールする
   */
  private scheduleSpectatorTurn(): void {
    if (this.battleState.isFinished) return;

    this.spectatorAutoTurnTimer?.remove();
    this.spectatorAutoTurnTimer = this.time.delayedCall(
      SPECTATOR_AUTO_TURN_DELAY_MS,
      () => this.processSpectatorTurn()
    );
  }

  /**
   * 観戦モード: 両CPUのコマンドを自動生成してターン処理する
   */
  private processSpectatorTurn(): void {
    if (this.battleState.isFinished) return;

    try {
      // CPU1（player1）のコマンド生成
      const p1AILevel = this.playerAIConfig?.thinkingLevel ?? this.enemyAILevel;
      const cpu1Commands = selectCommandsForBattle(
        this.battleState,
        'player1',
        this.playerMonster,
        p1AILevel,
        this.enemyMonster,
        this.turnHistory,
        this.playerAIConfig,
      );

      // CPU2（player2）のコマンド生成
      const p2AILevel = this.enemyAIConfig?.thinkingLevel ?? this.enemyAILevel2;
      const cpu2Commands = selectCommandsForBattle(
        this.battleState,
        'player2',
        this.enemyMonster,
        p2AILevel,
        this.playerMonster,
        this.turnHistory,
        this.enemyAIConfig,
      );

      // ターン処理
      const distanceBefore = this.battleState.currentDistance;
      const { newState, turnResult } = processTurn(
        this.battleState,
        this.playerMonster,
        this.enemyMonster,
        cpu1Commands,
        cpu2Commands
      );

      // エフェクト解決
      const effectSequence = resolveBattleEffects(turnResult, distanceBefore);

      // エフェクト再生
      this.isPlayingEffects = true;

      const startPlayerHp = this.playerCurrentHp;
      const startEnemyHp = this.enemyCurrentHp;
      const onPhaseComplete = (phaseIndex: number) => {
        if (phaseIndex === 0 && !turnResult.phases[1].isSkipped) {
          const p1HpAfterPhase1 = Math.max(0, startPlayerHp - turnResult.phases[0].player1Damage.damage);
          const p2HpAfterPhase1 = Math.max(0, startEnemyHp - turnResult.phases[0].player2Damage.damage);
          this.updateHp(p1HpAfterPhase1, p2HpAfterPhase1);
        }
      };

      this.effectPlayer.playSequence(effectSequence, onPhaseComplete).then(() => {
        this.applyTurnResult(newState, turnResult);
      }).catch((error) => {
        console.error('Spectator effect playback error:', error);
        this.applyTurnResult(newState, turnResult);
      });
    } catch (error) {
      console.error('Spectator turn processing error:', error);
      // エラーが発生しても観戦ループを継続する
      this.scheduleSpectatorTurn();
    }
  }

  /**
   * 観戦モード: 中断ボタンクリック処理
   */
  /**
   * AIオートモード: 次のターンをスケジュールする
   */
  private scheduleAutoPlayerTurn(): void {
    if (this.battleState.isFinished) return;
    this.spectatorAutoTurnTimer?.remove();
    this.spectatorAutoTurnTimer = this.time.delayedCall(
      1000,
      () => this.processAutoPlayerTurn()
    );
  }

  /**
   * AIオートモード: プレイヤー側をカスタムAIで操作してターン処理する
   */
  private processAutoPlayerTurn(): void {
    if (this.battleState.isFinished || !this.playerAIConfig) return;

    try {
      const playerAILevel = this.playerAIConfig.thinkingLevel;
      const playerCommands = selectCommandsForBattle(
        this.battleState,
        'player1',
        this.playerMonster,
        playerAILevel,
        this.enemyMonster,
        this.turnHistory,
        this.playerAIConfig,
      );
      this.processLocalTurn(playerCommands);
    } catch (error) {
      console.error('[AutoPlayer] error:', error);
      this.scheduleAutoPlayerTurn();
    }
  }

  private onSpectatorInterrupt(): void {
    if (this.battleState?.isFinished) return;

    this.showSpectatorInterruptDialog();
  }

  /**
   * 観戦モード: 中断確認ダイアログを表示
   */
  private showSpectatorInterruptDialog(): void {
    // 自動ターン進行を一時停止
    this.spectatorAutoTurnTimer?.remove();

    // 半透明オーバーレイ
    this.surrenderConfirmOverlay = this.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setDepth(80)
      .setInteractive();

    // パネル背景
    this.surrenderConfirmPanel = this.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, 320, 180, 0x222233)
      .setDepth(81)
      .setStrokeStyle(2, 0x888888);

    // タイトル
    this.surrenderConfirmTitle = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 50, SPECTATOR_MESSAGES.confirmTitle, {
        fontSize: '20px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(82);

    // 本文
    this.surrenderConfirmBody = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 15, SPECTATOR_MESSAGES.confirmBody, {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        align: 'center',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(82);

    // 「はい」ボタン
    this.surrenderConfirmYesBg = this.add
      .rectangle(GAME_WIDTH / 2 - 60, GAME_HEIGHT / 2 + 45, 90, 34, 0x884444)
      .setDepth(82)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.onSpectatorInterruptConfirm());

    this.surrenderConfirmYesText = this.add
      .text(GAME_WIDTH / 2 - 60, GAME_HEIGHT / 2 + 45, SURRENDER_MESSAGES.confirmYes, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(83);

    // 「いいえ」ボタン
    this.surrenderConfirmNoBg = this.add
      .rectangle(GAME_WIDTH / 2 + 60, GAME_HEIGHT / 2 + 45, 90, 34, 0x445588)
      .setDepth(82)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.dismissSpectatorInterruptDialog());

    this.surrenderConfirmNoText = this.add
      .text(GAME_WIDTH / 2 + 60, GAME_HEIGHT / 2 + 45, SURRENDER_MESSAGES.confirmNo, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(83);

    // 中断ボタンを無効化
    this.surrenderButtonBg.disableInteractive();
  }

  /**
   * 観戦モード: 中断確認ダイアログを閉じる（キャンセル）
   */
  private dismissSpectatorInterruptDialog(): void {
    this.surrenderConfirmOverlay?.destroy();
    this.surrenderConfirmPanel?.destroy();
    this.surrenderConfirmTitle?.destroy();
    this.surrenderConfirmBody?.destroy();
    this.surrenderConfirmYesBg?.destroy();
    this.surrenderConfirmYesText?.destroy();
    this.surrenderConfirmNoBg?.destroy();
    this.surrenderConfirmNoText?.destroy();
    this.surrenderConfirmOverlay = undefined;
    this.surrenderConfirmPanel = undefined;
    this.surrenderConfirmTitle = undefined;
    this.surrenderConfirmBody = undefined;
    this.surrenderConfirmYesBg = undefined;
    this.surrenderConfirmYesText = undefined;
    this.surrenderConfirmNoBg = undefined;
    this.surrenderConfirmNoText = undefined;

    // 中断ボタンを再有効化
    if (!this.battleState?.isFinished) {
      this.surrenderButtonBg.setInteractive({ useHandCursor: true });
      // 自動ターンを再スケジュール
      if (!this.isPlayingEffects) {
        this.scheduleSpectatorTurn();
      }
    }
  }

  /**
   * 観戦モード: 中断確定処理
   */
  private onSpectatorInterruptConfirm(): void {
    this.dismissSpectatorInterruptDialog();
    this.spectatorAutoTurnTimer?.remove();
    this.battleState.isFinished = true;
    this.surrenderButtonBg.disableInteractive();
    stopBgm();
    this.transitionTo(SceneKey.BATTLE_MODE_SELECT);
  }

  // --- ギブアップ ---

  /**
   * ギブアップボタンを生成する
   */
  private createSurrenderButton(): void {
    const x = 890;
    const y = GAME_HEIGHT - 15;
    const width = 100;
    const height = 28;

    this.surrenderButtonBg = this.add
      .rectangle(x, y, width, height, 0x664444)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.onSurrenderClick());

    this.surrenderButtonText = this.add
      .text(x, y, SURRENDER_MESSAGES.buttonLabel, {
        fontSize: '13px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5);
  }

  /**
   * ギブアップボタンクリック処理
   */
  private onSurrenderClick(): void {
    if (this.isPlayingEffects) return;
    if (this.battleState?.isFinished) return;

    this.showSurrenderConfirmDialog();
  }

  /**
   * ギブアップ確認ダイアログを表示する
   */
  private showSurrenderConfirmDialog(): void {
    const body = this.isNetworkMode
      ? SURRENDER_MESSAGES.onlineConfirmBody
      : SURRENDER_MESSAGES.confirmBody;

    // 半透明オーバーレイ
    this.surrenderConfirmOverlay = this.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setDepth(80)
      .setInteractive();

    // パネル背景
    this.surrenderConfirmPanel = this.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, 320, 180, 0x222233)
      .setDepth(81)
      .setStrokeStyle(2, 0x888888);

    // タイトル
    this.surrenderConfirmTitle = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 50, SURRENDER_MESSAGES.confirmTitle, {
        fontSize: '20px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(82);

    // 本文
    this.surrenderConfirmBody = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 15, body, {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        align: 'center',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(82);

    // 「はい」ボタン
    this.surrenderConfirmYesBg = this.add
      .rectangle(GAME_WIDTH / 2 - 60, GAME_HEIGHT / 2 + 45, 90, 34, 0x884444)
      .setDepth(82)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.onSurrenderConfirm());

    this.surrenderConfirmYesText = this.add
      .text(GAME_WIDTH / 2 - 60, GAME_HEIGHT / 2 + 45, SURRENDER_MESSAGES.confirmYes, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(83);

    // 「いいえ」ボタン
    this.surrenderConfirmNoBg = this.add
      .rectangle(GAME_WIDTH / 2 + 60, GAME_HEIGHT / 2 + 45, 90, 34, 0x445588)
      .setDepth(82)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.dismissSurrenderDialog());

    this.surrenderConfirmNoText = this.add
      .text(GAME_WIDTH / 2 + 60, GAME_HEIGHT / 2 + 45, SURRENDER_MESSAGES.confirmNo, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(83);

    // ダイアログ表示中はコマンドUI・ギブアップボタンを無効化
    this.setCommandUIEnabled(false);
    this.surrenderButtonBg.disableInteractive();
  }

  /**
   * ギブアップ確認ダイアログを閉じる
   */
  private dismissSurrenderDialog(): void {
    this.surrenderConfirmOverlay?.destroy();
    this.surrenderConfirmPanel?.destroy();
    this.surrenderConfirmTitle?.destroy();
    this.surrenderConfirmBody?.destroy();
    this.surrenderConfirmYesBg?.destroy();
    this.surrenderConfirmYesText?.destroy();
    this.surrenderConfirmNoBg?.destroy();
    this.surrenderConfirmNoText?.destroy();
    this.surrenderConfirmOverlay = undefined;
    this.surrenderConfirmPanel = undefined;
    this.surrenderConfirmTitle = undefined;
    this.surrenderConfirmBody = undefined;
    this.surrenderConfirmYesBg = undefined;
    this.surrenderConfirmYesText = undefined;
    this.surrenderConfirmNoBg = undefined;
    this.surrenderConfirmNoText = undefined;

    // コマンドUIとギブアップボタンを再有効化
    if (!this.battleState?.isFinished && !this.isPlayingEffects) {
      this.setCommandUIEnabled(true);
      this.surrenderButtonBg.setInteractive({ useHandCursor: true });
    }
  }

  /**
   * ギブアップ確定処理
   */
  private onSurrenderConfirm(): void {
    this.dismissSurrenderDialog();

    if (this.isNetworkMode && this.socketClient && this.roomId) {
      // 通信対戦: サーバーにsurrender送信
      this.socketClient.surrender(this.roomId);
      this.isConfirmProcessing = false;
      this.setCommandUIEnabled(false);
      this.surrenderButtonBg.disableInteractive();
      this.showWaitingMessage('ギブアップ処理中...');
      // サーバーからの BATTLE_FINISHED で結果画面に遷移
    } else {
      // CPUモード: ローカルでギブアップ処理
      const giveUpPlayer: 1 | 2 = 1; // プレイヤーは常にplayer1
      const battleResult = checkVictoryOnGiveUp(this.battleState, giveUpPlayer);
      this.battleState = battleResult.finalState;
      battleResult.turnHistory = this.turnHistory;
      this.isConfirmProcessing = false;
      this.setCommandUIEnabled(false);
      this.surrenderButtonBg.disableInteractive();
      this.transitionToResultWithDown(battleResult, {
        battleResult,
        mode: this.gameMode,
        stageNumber: this.stageNumber,
        clearedStages: this.clearedStages,
        monsterId: this.playerMonster.id,
        isReplay: this.isReplay,
      });
    }
  }
}
